import jsPDF from "jspdf";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import DateConverter from "@heartinz/date_and_time_processor";
import autoTable from "jspdf-autotable";
import { convertToGrayScale, loadImageDimensions, parseAmount } from "../modules/invoiceUtils";

export const Browser2InchInvoiceReceipt = async (
 data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings
) => {
   const {
    documentConfig,hasPayment       
  } = invoiceConfig;
  const mm = (pt) => pt / 2.83465;
  const taxRows1 =documentConfig?.items;
  const itemHeight =documentConfig?.items.length * mm(45); // Approx 45pt per item row
  const taxHeight = Object.keys(taxRows1).length * mm(25); //25
  const footerHeight = mm(250); // Total for branding, thank you, and payment boxes
  const headerHeight = mm(250); // Total for logo, store info, and invoice details

  // Total dynamic height with a small buffer
  const dynamicHeight = headerHeight + itemHeight + taxHeight + footerHeight;

  const doc = new jsPDF({
    unit: "pt",
    format: [144, 1000],
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 10;

  const isIGST =
    documentConfig?.fromData?.statecode &&
    documentConfig?.toData?.statecode &&
    documentConfig?.fromData.statecode !== documentConfig?.toData?.statecode;

  const taxRows =documentConfig?.items?.reduce((acc, product) => {
    const taxPercentage = product?.taxpercentage || 0;
    const taxAmount = product?.lineTotalTaxAmount || 0;
    const basePrice = product?.lineTotalBasePrice || 0;
    if (!acc[taxPercentage]) {
      acc[taxPercentage] = { basePrice: 0, sgst: 0, cgst: 0, igst: 0 };
    }
    acc[taxPercentage].basePrice += basePrice;
    if (isIGST) {
      acc[taxPercentage].igst += taxAmount;
    } else {
      acc[taxPercentage].sgst += taxAmount / 2;
      acc[taxPercentage].cgst += taxAmount / 2;
    }
    return acc;
  }, {});

  const checkPageBreak = (doc, y, buffer = 20) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + buffer >= pageHeight) {
      doc.addPage();
      return 14; // reset y to top margin for new page
    }
    return y;
  };

 const imagePath = documentConfig?.image;

  if (documentConfig?.image) {
    try {
      // Step 1: Convert image URL to base64
      const response = await fetch(imagePath, { mode: "cors" });
      const blob = await response.blob();
      let base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Step 2: Convert to grayscale
      base64 = await convertToGrayScale(base64);

      // Step 3: Get natural dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Step 4: Resize to fit dynamically within maxWidth and maxHeight
      const maxWidth = 50; // maximum width in pt for 3-inch receipt
      const maxHeight = 40; // maximum height in pt
      const widthRatio = maxWidth / naturalWidth;
      const heightRatio = maxHeight / naturalHeight;
      const ratio = Math.min(widthRatio, heightRatio, 1); // don't upscale

      const imgWidth = naturalWidth * ratio;
      const imgHeight = naturalHeight * ratio;

      // Step 5: Center the logo
      const logoX = (pageWidth - imgWidth) / 2;

      doc.addImage(base64, "PNG", logoX, y, imgWidth, imgHeight);

      y += imgHeight + 12; // spacing after logo
    } catch (err) {
      console.warn("Logo failed to load:", err);
    }
  }

   const invoiceTitle =
    data?.status === "CANCELLED"
      ? "CANCELLED TAX INVOICE"
      :documentConfig?.title;
;

  doc
    .setFont("NotoSansMono-Bold", "bold")
    .setFontSize(7)
    .text(invoiceTitle, pageWidth / 2, y, { align: "center" });
  y += 9;
  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(6);
  const storeTitle = documentConfig?.fromData?.title || "Store";
  const wrappedStoreTitle = doc.splitTextToSize(storeTitle, pageWidth - 20);
  wrappedStoreTitle.forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += 7;
  });
  doc.setFont("NotoSansMono-Regular", "normal");
  if (documentConfig?.fromData?.phone ||documentConfig?.fromData?.additionalPhone) {
    doc.text(`Ph:${documentConfig?.fromData?.phone
            ? `${documentConfig.fromData.phone}`
            : ""
        }${
          documentConfig?.fromData?.additionalPhone
            ? `, ${documentConfig.fromData.additionalPhone}`
            : ""
        } `, pageWidth / 2, y, {
      align: "center",
    });
    y += 7;
  }
  const seller = documentConfig?.fromData;
  const sellerAddressParts = [
    seller?.addressline1,
    [seller?.state, seller?.district, seller?.country]
      .filter(Boolean)
      .join(", "),
    seller?.pincode ? `- ${seller?.pincode}` : null,
  ].filter(Boolean);
  const sellerRawAddress = sellerAddressParts.join(", ");
  const wrappedSellerAddress = doc.splitTextToSize(
    sellerRawAddress,
    pageWidth - 20,
  );
  wrappedSellerAddress.forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += 7;
  });
  doc.setFont("NotoSansMono-Regular", "normal");
  if (documentConfig?.fromData?.email) {
    doc.text(`Email: ${documentConfig?.fromData?.email}`, pageWidth / 2, y, {
      align: "center",
    });
    y += 7;
  }
  doc.text(`GST: ${seller?.gstnumber || ""}`, pageWidth / 2, y, {
    align: "center",
  });
  y += 7;

  doc.setLineDash([1, 1]);
  doc.line(0, y, pageWidth - 5, y);
  doc.setLineDash([]);
  y += 8;
  const { date, time } = documentConfig?.Date
  const labelX = 0; // Left-aligned label start
  const colonX = 40; // Colon aligned at this point
  const valueX = 45; // Value starts after colon
  const lineHeight = 10;

  const drawRowWithColonAligned = (label, value, yPos, isBold = false) => {
    const font = isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular";
    doc.setFont(font, isBold ? "bold" : "normal").setFontSize(7);
    doc.text(label, labelX, yPos);
    doc.text(":", colonX, yPos);
    doc.text(value, valueX, yPos);
  };
  drawRowWithColonAligned(
   documentConfig?.numberLabel ,
    `${documentConfig?.number}`,
    y,
  );
  y += lineHeight;
  drawRowWithColonAligned("Date", `${date}, ${time}`, y);
  y += lineHeight;
  drawRowWithColonAligned("Amount", `${documentConfig?.grandTotal}`, y, true);
  y += 5;
  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(0, y, pageWidth - 5, y);
  y += 10;
  const buyer = documentConfig?.toData;
  const hasBuyerInfo =
    buyer &&
    (buyer?.name ||
      buyer?.phone ||
      buyer?.addressline1 ||
      buyer?.email ||
      buyer?.gstnumber ||
      buyer?.pannumber ||
      buyer?.cinnumber);

  if (hasBuyerInfo) {
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6.5);
    doc.text("BUYER INFORMATION", pageWidth / 2, y, { align: "center" });
    y += 10;

    doc.setFont("NotoSansMono-Regular", "normal");
    if (buyer?.name) {
      doc.text(`${buyer?.name}`, 0, y);
      y += 7;
    }
    if (buyer?.phone) {
      doc.text(`Ph: ${buyer?.phone}`, 0, y);
      y += 7;
    }
    const addressParts = [
      buyer?.addressline1,
      [buyer?.state, buyer?.district, buyer?.country]
        .filter(Boolean)
        .join(", "),
      buyer?.pincode ? `- ${buyer?.pincode}` : null,
    ].filter(Boolean);
    const rawAddressText = addressParts.join(", ");
    const wrappedAddress = doc.splitTextToSize(rawAddressText, pageWidth - 20);
    wrappedAddress.forEach((line) => {
      doc.text(line, 0, y);
      y += 7;
    });
    if (buyer?.email) {
      doc.text(`Email: ${buyer?.email}`, 0, y);
      y += 7;
    }
    if (buyer?.gstnumber) {
      doc.text(`GST: ${buyer?.gstnumber}`, 0, y);
      y += 7;
    }
    if (buyer?.pannumber) {
      doc.text(`PAN: ${buyer?.pannumber}`, 0, y);
      y += 7;
    }
    if (buyer?.cinnumber) {
      doc.text(`CIN: ${buyer?.cinnumber}`, 0, y);
      y += 7;
    }
    if (buyer?.fssai) {
      doc.text(`FSSAI: ${buyer?.cinnumber}`, 0, y);
      y += 7;
    }
   if (buyer?.referenceid) {
      doc.text(`Reference No: ${buyer?.referenceid}`, 0, y);
      y += 7;
   }
    doc.setLineDash([1, 1]);
    doc.line(0, y, pageWidth - 5, y);
    doc.setLineDash([]);
    y += 5;
  }
  const itemRows = [
    [
      {
        content: "",
        colSpan: 4,
        styles: {
          fontSize: 2,
          lineWidth: 0,
          halign: "center",
        },
      },
    ],
    // Actual item rows
    ...documentConfig?.items.flatMap((item) => {
      const rows = [];

      // Title row
      rows.push([
        {
          content: item?.title,
          colSpan: 4,
          styles: {
            fontSize: 6,
            fontStyle: "normal",
            font: "NotoSansMono-Regular",
          },
        },
      ]);

      // Note row (only if present and imode = PRODUCT)
      if (item?.imode === "PRODUCT" && item.note) {
        rows.push([
          {
            content: `(${item?.note})`,
            colSpan: 4,
            styles: {
              fontSize: 5,
              textColor: [100, 100, 100],
              fontStyle: "normal",
            },
          },
        ]);
      }

      // Price row
      rows.push([
        "",
        `${item?.soldprice}`,
        `${item?.quantity} ${(() => {
          const unitCode = item?.isSecondaryUnit
            ? item?.unitConfig?.secondaryUnitCode
            : item?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`,
        `${item?.lineTotalSoldPrice}`,
      ]);

      return rows;
    }),
  ];

  autoTable(doc, {
    startY: y,
    styles: {
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
    },
    head: [
      [
        {
          content: "Item",
          colSpan: 4,
          styles: {
            halign: "left",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
      [
        { content: "", styles: { cellPadding: 0 } },
        {
          content: "Rate",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Qty",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Total",
          styles: {
            halign: "right",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
    ],
    body: itemRows,
    theme: "plain",
    styles: {
      fontSize: 6,
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
      lineWidth: 0,
      font: "NotoSansMono-Regular",
      fontStyle: "normal",
    },
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "center" },
      2: { halign: "center" },
      3: { halign: "right" },
    },
    margin: { left: 0, right: 5 },
    didDrawCell: (data) => {
      const pageWidth = doc.internal.pageSize.getWidth();
      const left = 0;
      const right = pageWidth - 5;
      // Bottom dotted line after the second header row ("Rate Qty Total")
      if (
        data.row.section === "head" &&
        data.row.index === 1 &&
        data.column.index === 3
      ) {
        const bottom = data.cell.y + data.cell.height + 2;
        doc.setLineDash([1, 1]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        // doc.setLineDash([]);
      }
      // Bottom dotted line after last row
      if (
        data.row.section === "body" &&
        data.row.index === itemRows?.length - 1 &&
        data.column.index === 0
      ) {
        const bottom = data.cell.y + data.cell.height + 2;
        doc.setLineDash([1, 1]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        doc.setLineDash([]);
      }
    },
  });
  y = doc.lastAutoTable.finalY + 10;
  y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(7);
  doc.text(`Total Item: ${data?.totalItems}`, 0, y);
  y += 10;
  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(9);
  doc.text(`Grand Total: ${documentConfig?.grandTotal}`, 0, y, { align: "left" });
  y += 5;
  doc.setDrawColor(0); // light gray
  doc.setLineDash([1, 1]); // dash pattern
  doc.line(0, y, pageWidth - 7, y); // draw the line
  doc.setLineDash([]); // reset dash
  y += 10;
  y = checkPageBreak(doc, y);
  doc.setTextColor(0, 0, 0);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(8);
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text("**** YOU SAVED ****", pageWidth / 2, y, { align: "center" });
    y += 10;
  }
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text(`${documentConfig?.totalDiscount}`, pageWidth / 2, y, { align: "center" });
  }
  doc.setTextColor(0, 0, 0);
  y += 5;
  doc.setDrawColor(0); // light gray
  doc.setLineDash([1, 1]); // dash pattern
  doc.line(0, y, pageWidth - 7, y); // draw the line
  doc.setLineDash([]);
  y += 5;
  y = checkPageBreak(doc, y);
  const taxHead = isIGST
    ? [["Tax", "Amount", "IGST"]]
    : [["Tax", "Amount", "SGST", "CGST"]];

  const taxBody = Object.entries(taxRows)
    .filter(([percent]) => parseFloat(percent) > 0) // ✅ Skip 0% tax
    .map(([percent, t]) =>
      isIGST
        ? [`${percent}%`, `${t?.basePrice}`, `${t?.igst}`]
        : [`${percent}%`, `${t?.basePrice}`, `${t?.sgst}`, `${t?.cgst}`],
    );
  let tableRendered = false;
  if (taxBody?.length > 0) {
    tableRendered = true;

    autoTable(doc, {
      startY: y,
      head: taxHead.map((row) =>
        row.map((cell, colIndex) => ({
          content: cell,
          styles: {
            font: "NotoSansMono-Regular",
            fontStyle: "normal",
            fontSize: 6,
            halign: colIndex === 0 ? "left" : "right",
          },
        })),
      ),
      body: taxBody?.map((row) =>
        row.map((cell, colIndex) => ({
          content: cell,
          styles: {
            font: "NotoSansMono-Regular",
            fontStyle: "normal",
            fontSize: 6,
            halign: colIndex === 0 ? "left" : "right",
          },
        })),
      ),
      theme: "plain",
      styles: {
        font: "NotoSansMono-Regular",
        fontStyle: "normal",
        fontSize: 6,
        cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
        lineWidth: 0,
      },
      margin: { left: 0, right: 5 },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "right" },
        2: { halign: "right" },
        3: { halign: "right" },
      },
      didDrawCell: (data) => {
        const pageWidth = doc.internal.pageSize.getWidth();

        if (
          data.row.section === "head" &&
          data.row.index === 0 &&
          data.column.index === 0
        ) {
          const top = data.cell.y - 2;
          doc.setLineDash([1, 1]);
          doc.setDrawColor(0);
          doc.line(0, top, pageWidth - 5, top);
        }

        const isLastTaxRow =
          data.row.section === "body" && data.row.index === taxBody?.length - 1;

        if (isLastTaxRow && data.column.index === 0) {
          const bottom = data.cell.y + data.cell.height + 2;
          doc.setLineDash([1, 1]);
          doc.setDrawColor(0);
          doc.line(0, bottom, pageWidth - 5, bottom);
          doc.setLineDash([]);
        }
      },
    });
  }
  if (tableRendered && doc.lastAutoTable?.finalY) {
    y = doc.lastAutoTable.finalY + 10;
  }
  y = checkPageBreak(doc, y);
  if (parseAmount(documentConfig?.totalTaxAmount) > 0) {
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(7);
    doc.text("Total Tax", 0, y);
    doc.text(`${documentConfig?.totalTaxAmount}`, pageWidth - 5, y, { align: "right" });
    y += 5;
  }

  // -------- PAYMENT MODE HEADER --------
   if (hasPayment) {
  doc.setLineDash([1, 1]);
  doc.line(2, y, pageWidth - 2, y);
  doc.setLineDash([]);
  y += 10;

  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(7);
  doc.text("PAYMENT MODE", pageWidth / 2, y, { align: "center" });
  y += 8;

  // -------- PAYMENT VALUES --------
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6);

  // 2-inch aligned positions
  const payLabelX = 2; // label start
  const payColonX = 42; // aligned colon
  const payValueX = 46; // value after :

  const drawPaymentRow = (label, amount, isBold = false) => {
    if (parseAmount(amount) > 0) {
      y = checkPageBreak(doc, y);

      doc.setFont(
        isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular",
        isBold ? "bold" : "normal",
      );
      doc.setFontSize(6);

      doc.text(label, payLabelX, y);
      doc.text(":", payColonX, y);
      doc.text(`${amount}`, payValueX, y);

      y += 8;
    }
  };

  // -------- ADJUSTED VALUES --------
  const cashPaid = (data?.cashamount || 0) - (data?.cashchange || 0);

  const cardPaid = (data?.cardamount || 0) - (data?.cardchange || 0);

  const onlinePaid = (data?.onlineamount || 0) - (data?.onlinechange || 0);

  const upiPaid = (data?.upiamount || 0) - (data?.upichange || 0);

  const balanceDue = (data?.paylateramount || 0) - (data?.paylaterchange || 0);

  // -------- DRAW ROWS --------
  drawPaymentRow("Cash", documentConfig?.cashPaid);
  drawPaymentRow("Card",documentConfig?.cardPaid);
  drawPaymentRow("Online Pay", documentConfig?.onlinePaid);
  drawPaymentRow("UPI", documentConfig?.upiPaid);

  // -------- BALANCE DUE (BOLD) --------
  if (parseAmount(documentConfig?.balanceDue) > 0) {
    // y += (4);
    drawPaymentRow("Balance Due", documentConfig?.balanceDue, true);
  }
   }
  // -------- FOOTER LINE --------
  // y += (4);
  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(2, y, pageWidth - 2, y);
  doc.setLineDash([]);
  y += 7;

  y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6);
  doc.text("Thank you for your purchase!", pageWidth / 2, y, {
    align: "center",
  });
  y += 7;
  doc.setFont("NotoSansMono-Bold", "bold");
  doc.text("** Visit us Again **", pageWidth / 2, y, { align: "center" });
  y += 7;
  if (storeSettings?.negobillBranding) {
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text("Powered By", pageWidth / 2, y, { align: "center" });
    y += 7;
    doc.setFontSize(5);
    const sitePrefix = "www.";
    const siteBold = "NegoBill";
    const siteSuffix = ".com";
    const prefixWidth = doc.getTextWidth(sitePrefix);
    const boldWidth = doc.getTextWidth(siteBold);
    const suffixWidth = doc.getTextWidth(siteSuffix);
    const totalWidth = prefixWidth + boldWidth + suffixWidth;
    const startX = (pageWidth - totalWidth) / 2;
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(sitePrefix, startX, y);
    doc.setFont("NotoSansMono-Bold", "bold");
    doc.text(siteBold, startX + prefixWidth, y);
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(siteSuffix, startX + prefixWidth + boldWidth, y);
    y += 20;
  }
  // setPrint(false);
  return doc;
};

export const Browser2InchBillReceipt = async (data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings) => {
   const {
    documentConfig, hasPayment      
  } = invoiceConfig;
  const mm = (pt) => pt / 2.83465;
  const taxRows1 =documentConfig?.items;
  const itemHeight =documentConfig?.items?.length * mm(45); // Approx 45pt per item row
  const taxHeight = Object.keys(taxRows1)?.length * mm(25); //25
  const footerHeight = mm(250); // Total for branding, thank you, and payment boxes
  const headerHeight = mm(250); // Total for logo, store info, and invoice details

  // Total dynamic height with a small buffer
  const dynamicHeight = headerHeight + itemHeight + taxHeight + footerHeight;

  const doc = new jsPDF({
    unit: "pt",
    format: [144, 1000],
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 10;

  const isIGST =
    documentConfig?.fromData?.statecode &&
    documentConfig?.toData?.statecode &&
    documentConfig?.fromData?.statecode !== documentConfig?.toData?.statecode;

  const taxRows =documentConfig?.items?.reduce((acc, product) => {
    const taxPercentage = product?.taxpercentage || 0;
    const taxAmount = product?.lineTotalTaxAmount || 0;
    const basePrice = product?.lineTotalBasePrice || 0;
    if (!acc[taxPercentage]) {
      acc[taxPercentage] = { basePrice: 0, sgst: 0, cgst: 0, igst: 0 };
    }
    acc[taxPercentage].basePrice += basePrice;
    if (isIGST) {
      acc[taxPercentage].igst += taxAmount;
    } else {
      acc[taxPercentage].sgst += taxAmount / 2;
      acc[taxPercentage].cgst += taxAmount / 2;
    }
    return acc;
  }, {});

  const checkPageBreak = (doc, y, buffer = 20) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + buffer >= pageHeight) {
      doc.addPage();
      return 14; // reset y to top margin for new page
    }
    return y;
  };

 const imagePath =  documentConfig?.image;

  if (documentConfig?.image) {
    try {
      // Step 1: Convert image URL to base64
      const response = await fetch(imagePath, { mode: "cors" });
      const blob = await response.blob();
      let base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Step 2: Convert to grayscale
      base64 = await convertToGrayScale(base64);

      // Step 3: Get natural dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Step 4: Resize to fit dynamically within maxWidth and maxHeight
      const maxWidth = 50; // maximum width in pt for 3-inch receipt
      const maxHeight = 40; // maximum height in pt
      const widthRatio = maxWidth / naturalWidth;
      const heightRatio = maxHeight / naturalHeight;
      const ratio = Math.min(widthRatio, heightRatio, 1); // don't upscale

      const imgWidth = naturalWidth * ratio;
      const imgHeight = naturalHeight * ratio;

      // Step 5: Center the logo
      const logoX = (pageWidth - imgWidth) / 2;

      doc.addImage(base64, "PNG", logoX, y, imgWidth, imgHeight);

      y += imgHeight + 12; // spacing after logo
    } catch (err) {
      console.warn("Logo failed to load:", err);
    }
  }

  const invoiceTitle =
    data?.status === "CANCELLED"
      ? "CANCELLED TAX INVOICE"
      :documentConfig?.title;

  doc
    .setFont("NotoSansMono-Bold", "bold")
    .setFontSize(7)
    .text(invoiceTitle, pageWidth / 2, y, { align: "center" });
  y += 9;
  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(6);
  const storeTitle = documentConfig?.fromData?.title || "Store";
  const wrappedStoreTitle = doc.splitTextToSize(storeTitle, pageWidth - 20);
  wrappedStoreTitle.forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += 7;
  });
  doc.setFont("NotoSansMono-Regular", "normal");
  if (documentConfig?.fromData?.phone || documentConfig?.fromData?.additionalPhone) {
    doc.text(`Ph:${documentConfig?.fromData?.phone
            ? `${documentConfig.fromData.phone}`
            : ""
        }${
          documentConfig?.fromData?.additionalPhone
            ? `, ${documentConfig.fromData.additionalPhone}`
            : ""
        } `, pageWidth / 2, y, {
      align: "center",
    });
    y += 7;
  }
  const seller = documentConfig?.fromData;
  const sellerAddressParts = [
    seller?.addressline1,
    [seller?.state, seller?.district, seller?.country]
      .filter(Boolean)
      .join(", "),
    seller?.pincode ? `- ${seller?.pincode}` : null,
  ].filter(Boolean);
  const sellerRawAddress = sellerAddressParts.join(", ");
  const wrappedSellerAddress = doc.splitTextToSize(
    sellerRawAddress,
    pageWidth - 20,
  );
  wrappedSellerAddress.forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += 7;
  });
  doc.setFont("NotoSansMono-Regular", "normal");
  if (documentConfig?.fromData?.email) {
    doc.text(`Email: ${documentConfig?.fromData?.email}`, pageWidth / 2, y, {
      align: "center",
    });
    y += 7;
  }
  // doc.text(`GST: ${seller?.gstnumber || ""}`, pageWidth / 2, y, {
  //   align: "center",
  // });
  // y += (7);

  doc.setLineDash([1, 1]);
  doc.line(0, y, pageWidth - 5, y);
  doc.setLineDash([]);
  y += 8;
  const { date, time } = documentConfig?.Date
  const labelX = 0; // Left-aligned label start
  const colonX = 40; // Colon aligned at this point
  const valueX = 45; // Value starts after colon
  const lineHeight = 10;

  const drawRowWithColonAligned = (label, value, yPos, isBold = false) => {
    const font = isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular";
    doc.setFont(font, isBold ? "bold" : "normal").setFontSize(7);
    doc.text(label, labelX, yPos);
    doc.text(":", colonX, yPos);
    doc.text(value, valueX, yPos);
  };
  drawRowWithColonAligned(documentConfig?.numberLabel, `${documentConfig?.number}`, y);
  y += lineHeight;
  drawRowWithColonAligned("Date", `${date}, ${time}`, y);
  y += lineHeight;
  drawRowWithColonAligned("Amount", `${documentConfig?.grandTotal}`, y, true);
  y += 5;
  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(0, y, pageWidth - 5, y);
  y += 10;
  const buyer = documentConfig?.toData;
  const hasBuyerInfo =
    buyer &&
    (buyer?.name ||
      buyer?.phone ||
      buyer?.addressline1 ||
      buyer?.email ||
      buyer?.gstnumber ||
      buyer?.pannumber ||
      buyer?.cinnumber);

  if (hasBuyerInfo) {
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6.5);
    doc.text("BUYER INFORMATION", pageWidth / 2, y, { align: "center" });
    y += 10;

    doc.setFont("NotoSansMono-Regular", "normal");
    if (buyer?.name) {
      doc.text(`${buyer?.name}`, 0, y);
      y += 7;
    }
    if (buyer?.phone) {
      doc.text(`Ph: ${buyer?.phone}`, 0, y);
      y += 7;
    }
    const addressParts = [
      buyer?.addressline1,
      [buyer?.state, buyer?.district, buyer?.country]
        .filter(Boolean)
        .join(", "),
      buyer?.pincode ? `- ${buyer?.pincode}` : null,
    ].filter(Boolean);
    const rawAddressText = addressParts.join(", ");
    const wrappedAddress = doc.splitTextToSize(rawAddressText, pageWidth - 20);
    wrappedAddress.forEach((line) => {
      doc.text(line, 0, y);
      y += 7;
    });
    if (buyer?.email) {
      doc.text(`Email: ${buyer?.email}`, 0, y);
      y += 7;
    }
    if (buyer?.gstnumber) {
      doc.text(`GST: ${buyer?.gstnumber}`, 0, y);
      y += 7;
    }
    if (buyer?.pannumber) {
      doc.text(`PAN: ${buyer?.pannumber}`, 0, y);
      y += 7;
    }
    if (buyer?.cinnumber) {
      doc.text(`CIN: ${buyer?.cinnumber}`, 0, y);
      y += 7;
    }
    if (buyer?.fssai) {
      doc.text(`FSSAI: ${buyer?.cinnumber}`, 0, y);
      y += 7;
    }
   if (buyer?.referenceid) {
      doc.text(`Reference No: ${buyer?.referenceid}`, 0, y);
      y += 7;
   }
    doc.setLineDash([1, 1]);
    doc.line(0, y, pageWidth - 5, y);
    doc.setLineDash([]);
    y += 5;
  }
  const itemRows = [
    [
      {
        content: "",
        colSpan: 4,
        styles: {
          fontSize: 2,
          lineWidth: 0,
          halign: "center",
        },
      },
    ],
    // Actual item rows
    ...documentConfig?.items?.flatMap((item) => {
      const rows = [];

      // Title row
      rows.push([
        {
          content: item?.title,
          colSpan: 4,
          styles: {
            fontSize: 6,
            fontStyle: "normal",
            font: "NotoSansMono-Regular",
          },
        },
      ]);

      // Note row (only if present and imode = PRODUCT)
      if (item?.imode === "PRODUCT" && item?.note) {
        rows.push([
          {
            content: `(${item?.note})`,
            colSpan: 4,
            styles: {
              fontSize: 5,
              textColor: [100, 100, 100],
              fontStyle: "normal",
            },
          },
        ]);
      }

      // Price row
      rows.push([
        "",
        `${item?.soldprice}`,
        `${item?.quantity} ${(() => {
          const unitCode = item?.isSecondaryUnit
            ? item?.unitConfig?.secondaryUnitCode
            : item?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`,
        `${item?.lineTotalSoldPrice}`,
      ]);

      return rows;
    }),
  ];

  autoTable(doc, {
    startY: y,
    styles: {
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
    },
    head: [
      [
        {
          content: "Item",
          colSpan: 4,
          styles: {
            halign: "left",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
      [
        { content: "", styles: { cellPadding: 0 } },
        {
          content: "Rate",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Qty",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Total",
          styles: {
            halign: "right",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
    ],
    body: itemRows,
    theme: "plain",
    styles: {
      fontSize: 6,
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
      lineWidth: 0,
      font: "NotoSansMono-Regular",
      fontStyle: "normal",
    },
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "center" },
      2: { halign: "center" },
      3: { halign: "right" },
    },
    margin: { left: 0, right: 5 },
    didDrawCell: (data) => {
      const pageWidth = doc.internal.pageSize.getWidth();
      const left = 0;
      const right = pageWidth - 5;
      // Bottom dotted line after the second header row ("Rate Qty Total")
      if (
        data.row.section === "head" &&
        data.row.index === 1 &&
        data.column.index === 3
      ) {
        const bottom = data.cell.y + data.cell.height + 2;
        doc.setLineDash([1, 1]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        // doc.setLineDash([]);
      }
      // Bottom dotted line after last row
      if (
        data.row.section === "body" &&
        data.row.index === itemRows?.length - 1 &&
        data.column.index === 0
      ) {
        const bottom = data.cell.y + data.cell.height + 2;
        doc.setLineDash([1, 1]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        doc.setLineDash([]);
      }
    },
  });
  y = doc.lastAutoTable.finalY + 10;
  y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(7);
  doc.text(`Total Item: ${data?.totalItems}`, 0, y);
  y += 10;
  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(9);
  doc.text(`Grand Total: ${documentConfig?.grandTotal}`, 0, y, { align: "left" });
  y += 5;
  doc.setDrawColor(0); // light gray
  doc.setLineDash([1, 1]); // dash pattern
  doc.line(0, y, pageWidth - 7, y); // draw the line
  doc.setLineDash([]); // reset dash
  y += 10;
  y = checkPageBreak(doc, y);
  doc.setTextColor(0, 0, 0);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(8);
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text("**** YOU SAVED ****", pageWidth / 2, y, { align: "center" });
    y += 10;
  }
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text(`${documentConfig?.totalDiscount}`, pageWidth / 2, y, { align: "center" });
  }
  doc.setTextColor(0, 0, 0);
  y += 5;
  // doc.setDrawColor(0); // light gray
  // doc.setLineDash([(1), (1)]); // dash pattern
  // doc.line(0, y, pageWidth - (7), y); // draw the line
  // doc.setLineDash([]);
  // y += (5);
  // y = checkPageBreak(doc, y);
  // const taxHead = isIGST
  //   ? [["Tax", "Amount", "IGST"]]
  //   : [["Tax", "Amount", "SGST", "CGST"]];

  // const taxBody = Object.entries(taxRows)
  //   .filter(([percent]) => parseFloat(percent) > 0) // ✅ Skip 0% tax
  //   .map(([percent, t]) =>
  //     isIGST
  //       ? [
  //           `${percent}%`,
  //           `${CurrencyConverter.toCurrency(t.basePrice || 0, "INR")}`,
  //           `${CurrencyConverter.toCurrency(t.igst || 0, "INR")}`,
  //         ]
  //       : [
  //           `${percent}%`,
  //           `${CurrencyConverter.toCurrency(t.basePrice || 0, "INR")}`,
  //           `${CurrencyConverter.toCurrency(t.sgst || 0, "INR")}`,
  //           `${CurrencyConverter.toCurrency(t.cgst || 0, "INR")}`,
  //         ],
  //   );
  // let tableRendered = false;
  // if (taxBody.length > 0) {
  //   tableRendered = true;

  //   autoTable(doc, {
  //     startY: y,
  //     head: taxHead.map((row) =>
  //       row.map((cell, colIndex) => ({
  //         content: cell,
  //         styles: {
  //           font: "NotoSansMono-Regular",
  //           fontStyle: "normal",
  //           fontSize: 6,
  //           halign: colIndex === 0 ? "left" : "right",
  //         },
  //       })),
  //     ),
  //     body: taxBody.map((row) =>
  //       row.map((cell, colIndex) => ({
  //         content: cell,
  //         styles: {
  //           font: "NotoSansMono-Regular",
  //           fontStyle: "normal",
  //           fontSize: 6,
  //           halign: colIndex === 0 ? "left" : "right",
  //         },
  //       })),
  //     ),
  //     theme: "plain",
  //     styles: {
  //       font: "NotoSansMono-Regular",
  //       fontStyle: "normal",
  //       fontSize: 6,
  //       cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
  //       lineWidth: 0,
  //     },
  //     margin: { left: 0, right: (5) },
  //     columnStyles: {
  //       0: { halign: "left" },
  //       1: { halign: "right" },
  //       2: { halign: "right" },
  //       3: { halign: "right" },
  //     },
  //     didDrawCell: (data) => {
  //       const pageWidth = doc.internal.pageSize.getWidth();

  //       if (
  //         data.row.section === "head" &&
  //         data.row.index === 0 &&
  //         data.column.index === 0
  //       ) {
  //         const top = data.cell.y - (2);
  //         doc.setLineDash([(1), (1)]);
  //         doc.setDrawColor(0);
  //         doc.line(0, top, pageWidth - (5), top);
  //       }

  //       const isLastTaxRow =
  //         data.row.section === "body" &&
  //         data.row.index === taxBody.length - 1;

  //       if (isLastTaxRow && data.column.index === 0) {
  //         const bottom = data.cell.y + data.cell.height + (2);
  //         doc.setLineDash([(1), (1)]);
  //         doc.setDrawColor(0);
  //         doc.line(0, bottom, pageWidth - (5), bottom);
  //         doc.setLineDash([]);
  //       }
  //     },
  //   });
  // }
  // if (tableRendered && doc.lastAutoTable?.finalY) {
  //   y = doc.lastAutoTable.finalY + (10);
  // }
  // y = checkPageBreak(doc, y);
  // if (parseFloat(data.totaltaxamount) > 0) {
  //   doc.setFont("NotoSansMono-Regular", "normal").setFontSize(7);
  //   doc.text("Total Tax", 0, y);
  //   doc.text(
  //     `${CurrencyConverter.toCurrency(data.totaltaxamount || 0, "INR")}`,
  //     pageWidth - (5),
  //     y,
  //     { align: "right" },
  //   );
  //   y += (5);
  // }

  // -------- PAYMENT MODE HEADER --------
  if (hasPayment) {
  doc.setLineDash([1, 1]);
  doc.line(2, y, pageWidth - 2, y);
  doc.setLineDash([]);
  y += 10;

  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(7);
  doc.text("PAYMENT MODE", pageWidth / 2, y, { align: "center" });
  y += 8;

  // -------- PAYMENT VALUES --------
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6);

  // 2-inch aligned positions
  const payLabelX = 2; // label start
  const payColonX = 42; // aligned colon
  const payValueX = 46; // value after :

  const drawPaymentRow = (label, amount, isBold = false) => {
    if (parseAmount(amount) > 0) {
      y = checkPageBreak(doc, y);

      doc.setFont(
        isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular",
        isBold ? "bold" : "normal",
      );
      doc.setFontSize(6);

      doc.text(label, payLabelX, y);
      doc.text(":", payColonX, y);
      doc.text(`${amount}`, payValueX, y);

      y += 8;
    }
  };

  // -------- ADJUSTED VALUES --------
  const cashPaid = (data?.cashamount || 0) - (data?.cashchange || 0);

  const cardPaid = (data?.cardamount || 0) - (data?.cardchange || 0);

  const onlinePaid = (data?.onlineamount || 0) - (data?.onlinechange || 0);

  const upiPaid = (data?.upiamount || 0) - (data?.upichange || 0);

  const balanceDue = (data?.paylateramount || 0) - (data?.paylaterchange || 0);

  // -------- DRAW ROWS --------
  drawPaymentRow("Cash", documentConfig?.cashPaid);
  drawPaymentRow("Card", documentConfig?.cardPaid);
  drawPaymentRow("Online Pay", documentConfig?.onlinePaid);
  drawPaymentRow("UPI", documentConfig?.upiPaid);

  // -------- BALANCE DUE (BOLD) --------
  if (parseAmount(documentConfig?.balanceDue) > 0) {
    // y += (4);
    drawPaymentRow("Balance Due", documentConfig?.balanceDue, true);
  }
  }
  // -------- FOOTER LINE --------
  // y += (4);
  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(2, y, pageWidth - 2, y);
  doc.setLineDash([]);
  y += 7;

  y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6);
  doc.text("Thank you for your purchase!", pageWidth / 2, y, {
    align: "center",
  });
  y += 7;
  doc.setFont("NotoSansMono-Bold", "bold");
  doc.text("** Visit us Again **", pageWidth / 2, y, { align: "center" });
  y += 7;
  if (storeSettings?.negobillBranding) {
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text("Powered By", pageWidth / 2, y, { align: "center" });
    y += 7;
    doc.setFontSize(5);
    const sitePrefix = "www.";
    const siteBold = "NegoBill";
    const siteSuffix = ".com";
    const prefixWidth = doc.getTextWidth(sitePrefix);
    const boldWidth = doc.getTextWidth(siteBold);
    const suffixWidth = doc.getTextWidth(siteSuffix);
    const totalWidth = prefixWidth + boldWidth + suffixWidth;
    const startX = (pageWidth - totalWidth) / 2;
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(sitePrefix, startX, y);
    doc.setFont("NotoSansMono-Bold", "bold");
    doc.text(siteBold, startX + prefixWidth, y);
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(siteSuffix, startX + prefixWidth + boldWidth, y);
    y += 20;
  }
  // setPrint(false);
  return doc;
};

export const Browser3InchInvoiceReceipt = async (
 data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings
) => {
   const {
    documentConfig,  hasPayment     
  } = invoiceConfig;

  const mm = (pt) => pt / 2.83465;
  const taxRows1 =documentConfig?.items;
  const itemHeight =documentConfig?.items?.length * mm(45); // Approx 45pt per item row
  const taxHeight = Object.keys(taxRows1)?.length * mm(25); //25
  const footerHeight = mm(250); // Total for branding, thank you, and payment boxes
  const headerHeight = mm(250); // Total for logo, store info, and invoice details

  // Total dynamic height with a small buffer
  const dynamicHeight = headerHeight + itemHeight + taxHeight + footerHeight;
  const doc = new jsPDF({
    unit: "pt",
    format: [204, 1000],
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 5;

  const isIGST =
    documentConfig?.fromData?.statecode &&
    documentConfig?.toData?.statecode &&
    documentConfig?.fromData.statecode !== documentConfig?.toData?.statecode;

  const taxRows =documentConfig?.items?.reduce((acc, product) => {
    const taxPercentage = product?.taxpercentage || 0;
    const taxAmount = product?.lineTotalTaxAmount || 0;
    const basePrice = product?.lineTotalBasePrice || 0;
    if (!acc[taxPercentage]) {
      acc[taxPercentage] = { basePrice: 0, sgst: 0, cgst: 0, igst: 0 };
    }
    acc[taxPercentage].basePrice += basePrice;
    if (isIGST) {
      acc[taxPercentage].igst += taxAmount;
    } else {
      acc[taxPercentage].sgst += taxAmount / 2;
      acc[taxPercentage].cgst += taxAmount / 2;
    }
    return acc;
  }, {});

  const checkPageBreak = (doc, y, buffer = 20) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + buffer >= pageHeight) {
      doc.addPage();
      return 14; // reset y to top margin for new page
    }
    return y;
  };

 const imagePath = documentConfig?.image;

  if (documentConfig?.image) {
    try {
      // Step 1: Convert image URL to base64
      const response = await fetch(imagePath, { mode: "cors" });
      const blob = await response.blob();
      let base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Step 2: Convert to grayscale
      base64 = await convertToGrayScale(base64);

      // Step 3: Get natural dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Step 4: Resize to fit dynamically within maxWidth and maxHeight
      const maxWidth = 65; // maximum width in pt for 3-inch receipt
      const maxHeight = 40; // maximum height in pt
      const widthRatio = maxWidth / naturalWidth;
      const heightRatio = maxHeight / naturalHeight;
      const ratio = Math.min(widthRatio, heightRatio, 1); // don't upscale

      const imgWidth = naturalWidth * ratio;
      const imgHeight = naturalHeight * ratio;

      // Step 5: Center the logo
      const logoX = (pageWidth - imgWidth) / 2;

      doc.addImage(base64, "PNG", logoX, y, imgWidth, imgHeight);

      y += imgHeight + 10; // spacing after logo
    } catch (err) {
      console.warn("Logo failed to load:", err);
    }
  }

   const invoiceTitle =
    data?.status === "CANCELLED"
      ? "CANCELLED TAX INVOICE"
      :documentConfig?.title;


  doc
    .setFont("NotoSansMono-Bold", "bold")
    .setFontSize(10)
    .text(invoiceTitle, pageWidth / 2, y, { align: "center" });
  y += 13;

  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(12);
  const storeTitle = documentConfig?.fromData?.title || "Store";
  const wrappedStoreTitle = doc.splitTextToSize(storeTitle, pageWidth - 20);
  wrappedStoreTitle.forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += 10;
  });

  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);
  if (documentConfig?.fromData?.phone ||documentConfig?.fromData?.additionalPhone) {
    doc.text(`Ph:${documentConfig?.fromData?.phone
            ? `${documentConfig.fromData.phone}`
            : ""
        }${
          documentConfig?.fromData?.additionalPhone
            ? `, ${documentConfig.fromData.additionalPhone}`
            : ""
        } `, pageWidth / 2, y, {
      align: "center",
    });
    y += 8;
  }

  const seller = documentConfig?.fromData;
  const sellerAddressParts = [
    seller?.addressline1,
    [seller?.state, seller?.district, seller?.country]
      .filter(Boolean)
      .join(", "),
    seller?.pincode ? `- ${seller?.pincode}` : null,
  ].filter(Boolean);

  doc
    .splitTextToSize(sellerAddressParts.join(", "), pageWidth - 20)
    .forEach((line) => {
      doc.text(line, pageWidth / 2, y, { align: "center" });
      y += 10;
    });

  if (seller?.email) {
    doc.text(`Email: ${seller?.email}`, pageWidth / 2, y, { align: "center" });
    y += 10;
  }
  doc.text(`GST: ${seller?.gstnumber || ""}`, pageWidth / 2, y, {
    align: "center",
  });
  y += 6;
  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(5, y, pageWidth - 5, y);
  doc.setLineDash([]);
  y += 12;

  const { date, time } = documentConfig?.Date
  const labelX = 5; // Left-aligned label start
  const colonX = 65; // Colon aligned at this point
  const valueX = 70; // Value starts after colon
  const lineHeight = 8;
  const drawRowWithColonAligned = (label, value, yPos, isBold = false) => {
    const font = isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular";
    doc.setFont(font, isBold ? "bold" : "normal").setFontSize(8);
    doc.text(label, labelX, yPos);
    doc.text(":", colonX, yPos);
    doc.text(value, valueX, yPos);
  };
  drawRowWithColonAligned(
     documentConfig?.numberLabel ,
    `${documentConfig?.number}`,
    y,
  );
  y += lineHeight;
  drawRowWithColonAligned("Date", `${date}, ${time}`, y);
  y += lineHeight;
  drawRowWithColonAligned("Amount", `${documentConfig?.grandTotal}`, y, true);
  y += 6;
  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(5, y, pageWidth - 5, y);
  y += 11;

  const buyer = documentConfig?.toData;
  const hasBuyerInfo =
    buyer &&
    (buyer?.name ||
      buyer?.phone ||
      buyer?.addressline1 ||
      buyer?.email ||
      buyer?.gstnumber ||
      buyer?.pannumber ||
      buyer?.cinnumber);

  if (hasBuyerInfo) {
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);
    doc.text("BUYER INFORMATION", pageWidth / 2, y, { align: "center" });
    y += 9;

    if (buyer?.name) {
      doc.text(`${buyer?.name}`, 10, y);
      y += 8;
    }
    if (buyer?.phone) {
      doc.text(`Ph: ${buyer?.phone}`, 10, y);
      y += 8;
    }

    const addressParts = [
      buyer?.addressline1,
      [buyer?.state, buyer?.district, buyer?.country]
        .filter(Boolean)
        .join(", "),
      buyer?.pincode ? `- ${buyer?.pincode}` : null,
    ].filter(Boolean);

    doc
      .splitTextToSize(addressParts.join(", "), pageWidth - 20)
      .forEach((line) => {
        doc.text(line, 10, y);
        y += 8;
      });

    if (buyer?.email) {
      doc.text(`Email: ${buyer?.email}`, 10, y);
      y += 8;
    }
    if (buyer?.gstnumber) {
      doc.text(`GST: ${buyer?.gstnumber}`, 10, y);
      y += 8;
    }
    if (buyer?.pannumber) {
      doc.text(`PAN: ${buyer?.pannumber}`, 10, y);
      y += 8;
    }
    if (buyer?.cinnumber) {
      doc.text(`CIN: ${buyer?.cinnumber}`, 10, y);
      y += 8;
    }
   if (buyer?.fssai) {
      doc.text(`FSSAI: ${buyer?.fssai}`, 10, y);
      y += 8;
    }
    if (buyer?.referenceid) {
      doc.text(`Reference No: ${buyer?.referenceid}`, 10, y);
      y += 8;
    }
    doc.setDrawColor(0);
    doc.setLineDash([1, 1]);
    doc.line(5, y, pageWidth - 5, y);
    doc.setLineDash([]);
    y += 4;
  }
  const itemRows = [
    [
      {
        content: "",
        colSpan: 4,
        styles: {
          fontSize: 2,
          lineWidth: 0,
          halign: "center",
        },
      },
    ],
    // Actual item rows
    ...documentConfig?.items.flatMap((item) => {
      const rows = [];

      // Title row
      rows.push([
        {
          content: item.title,
          colSpan: 4,
          styles: {
            fontSize: 9,
            fontStyle: "normal",
            font: "NotoSansMono-Regular",
          },
        },
      ]);

      // Note row (only if present and imode = PRODUCT)
      // if (item.imode === "PRODUCT" && item.note) {
      //   rows.push([
      //     {
      //       content: `(${item.note})`,
      //       colSpan: 4,
      //       styles: {
      //         fontSize: 5,
      //         textColor: [100, 100, 100],
      //         fontStyle: "normal",
      //       },
      //     },
      //   ]);
      // }

      // Price row
      rows.push([
        "",
        `${item?.soldprice}`,
        `${item?.quantity} ${(() => {
          const unitCode = item?.isSecondaryUnit
            ? item?.unitConfig?.secondaryUnitCode
            : item?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`,
        `${item?.lineTotalSoldPrice}`,
      ]);

      return rows;
    }),
  ];

  autoTable(doc, {
    startY: y,
    styles: {
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
    },
    head: [
      [
        {
          content: "Item",
          colSpan: 4,
          styles: {
            halign: "left",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
      [
        { content: "", styles: { cellPadding: 0 } },
        {
          content: "Rate",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Qty",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Total",
          styles: {
            halign: "right",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
    ],
    body: itemRows,
    theme: "plain",
    styles: {
      fontSize: 9,
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
      lineWidth: 0,
      font: "NotoSansMono-Regular",
      fontStyle: "normal",
    },
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "center" },
      2: { halign: "center" },
      3: { halign: "right" },
    },
    margin: { left: 0, right: 5 },
    didDrawCell: (data) => {
      const pageWidth = doc.internal.pageSize.getWidth();
      const left = 0;
      const right = pageWidth - 5;
      // Bottom dotted line after the second header row ("Rate Qty Total")
      if (
        data.row.section === "head" &&
        data.row.index === 1 &&
        data.column.index === 3
      ) {
        const bottom = data.cell.y + data.cell.height + 2;
        doc.setLineDash([1, 1]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        // doc.setLineDash([]);
      }
      // Bottom dotted line after last row
      if (
        data.row.section === "body" &&
        data.row.index === itemRows?.length - 1 &&
        data.column.index === 0
      ) {
        const bottom = data.cell.y + data.cell.height + 2;
        doc.setLineDash([1, 1]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        doc.setLineDash([]);
      }
    },
  });
  y = doc.lastAutoTable.finalY + 10;
  y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(8);
  doc.text(`Total Item: ${data?.totalItems}`, 0, y);
  y += 10;
  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(10);
  doc.text(`Grand Total: ${documentConfig?.grandTotal}`, 0, y, { align: "left" });
  y += 5;
  // const itemRows = [
  //   [
  //     {
  //       content: "",
  //       colSpan: 4,
  //       styles: {
  //         fontSize: 9,
  //         // cellPadding: 2,
  //         lineWidth: 0,
  //         halign: "center",
  //       },
  //     },
  //   ],
  //   ...data.saleitems.flatMap((item) => {
  //     const rows = [];

  //     // Title + Note in single line
  //     let titleWithNote = item.title;
  //     if (item.imode === "PRODUCT" && item.note) {
  //       titleWithNote += ` (${item.note})`;
  //     }

  //     rows.push([
  //       {
  //         content: titleWithNote,
  //         colSpan: 4,
  //       },
  //     ]);

  //     // Price row
  //     rows.push([
  //       "",
  //       `${CurrencyConverter.toCurrency(item.soldprice || 0, "INR")}`,
  //       `${item.quantity}`,
  //       `${CurrencyConverter.toCurrency(
  //         item.lineTotalSoldPrice || 0,
  //         "INR",
  //       )}`,
  //     ]);

  //     return rows;
  //   }),
  // ];

  // autoTable(doc, {
  //   startY: y,
  //   pageBreak: "auto",
  //   styles:{
  //     cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
  //   },
  //   head: [
  //     [
  //       {
  //         content: "Item",
  //         colSpan: 4,
  //         styles: { halign: "left", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //     ],
  //     [
  //       { content: "", styles: { cellPadding: 0 } },
  //       {
  //         content: "Rate",
  //         styles: { halign: "center", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //       {
  //         content: "Qty",
  //         styles: { halign: "center", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //       {
  //         content: "Total",
  //         styles: { halign: "right", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //     ],
  //   ],
  //   body: itemRows,
  //   theme: "plain",
  //   styles: {
  //     fontSize: 12,
  //     styles:{
  //       cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
  //     },
  //     lineWidth: 0,
  //     font: "NotoSansMono-Regular",
  //     fontStyle: "normal",
  //   },
  //   columnStyles: {
  //     0: { halign: "left" },
  //     1: { halign: "center" },
  //     2: { halign: "center" },
  //     3: { halign: "right" },
  //   },
  //   margin: { left: (5), right: (5) },
  //   didDrawCell: (data) => {
  //     const pageWidth = doc.internal.pageSize.getWidth();
  //     const left = (5);
  //     const right = pageWidth - (5);
  //     // Bottom dotted line after the second header row ("Rate Qty Total")
  //     if (
  //       data.row.section === "head" &&
  //       data.row.index === 1 &&
  //       data.column.index === 3
  //     ) {
  //       const bottom = data.cell.y + data.cell.height + 2;
  //       doc.setDrawColor(0);
  //       doc.setLineDash([(1), (1)]);
  //       doc.line(left, bottom, right, bottom);
  //       doc.setLineDash([]);
  //     }
  //     // Bottom dotted line after last row
  //     if (
  //       data.row.section === "body" &&
  //       data.row.index === itemRows.length - 1 &&
  //       data.column.index === 0
  //     ) {
  //       const bottom = data.cell.y + data.cell.height + (2);
  //       doc.setDrawColor(0);
  //       doc.setLineDash([(1), (1)]);
  //       doc.line(left, bottom, right, bottom);
  //       doc.setLineDash([]);
  //     }
  //   },
  // });

  // y = doc.lastAutoTable.finalY + (20);
  // // y = checkPageBreak(doc, y);
  // doc.setFont("NotoSansMono-Regular", "normal");
  // doc.setFontSize(10);
  // doc.text(`Total Item: ${data.totalItems}`, (5), y);
  // y += (15);

  // doc.setFont("NotoSansMono-Bold", "bold").setFontSize(14);
  // doc.text(
  //   `Total Amount: ${CurrencyConverter.toCurrency(
  //     data.grandtotal || 0,
  //     "INR",
  //   )}.00`,
  //   (5),
  //   y,
  // );
  // y += (12);

  doc.setLineDash([1, 1]);
  doc.setDrawColor(0);
  doc.line(7, y, pageWidth - 7, y);
  doc.setLineDash([]);
  y += 11;

  y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(10);
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text("**** YOU SAVED ****", pageWidth / 2, y, { align: "center" });
    y += 10;
  }
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text(`${documentConfig?.totalDiscount}`, pageWidth / 2, y, { align: "center" });
  }
  y += 10;
  y = checkPageBreak(doc, y);
  // TAX SUMMARY
  const taxHead = isIGST
    ? [["Tax", "Amount", "IGST"]]
    : [["Tax", "Amount", "SGST", "CGST"]];

  const taxBody = Object.entries(taxRows)
    .filter(([percent]) => parseFloat(percent) > 0)
    .map(([percent, t]) =>
      isIGST
        ? [`${percent}%`, `${t?.basePrice}`, `${t?.igst}`]
        : [`${percent}%`, `${t?.basePrice}`, `${t?.sgst}`, `${t?.cgst}`],
    );

  if (taxBody.length > 0) {
    autoTable(doc, {
      startY: y,
      pageBreak: "auto",
      head: [
        taxHead[0].map((cell, colIndex) => ({
          content: cell,
          styles: {
            font: "NotoSansMono-Regular",
            fontStyle: "normal",
            fontSize: 8,
            halign: colIndex === 0 ? "left" : "right",
          },
        })),
      ],
      body: taxBody.map((row) =>
        row.map((cell, colIndex) => ({
          content: cell,
          styles: {
            font: "NotoSansMono-Regular",
            fontStyle: "normal",
            fontSize: 8,
            halign: colIndex === 0 ? "left" : "right",
          },
        })),
      ),
      theme: "plain",
      styles: {
        font: "NotoSansMono-Regular",
        fontStyle: "normal",
        fontSize: 9,
        cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
        lineWidth: 0,
      },
      margin: { left: 5, right: 5 },
      didDrawCell: (data) => {
        const pageWidth = doc.internal.pageSize.getWidth();

        if (
          data.row.section === "head" &&
          data.row.index === 0 &&
          data.column.index === 0
        ) {
          const top = data.cell.y - 2;
          doc.setLineDash([1, 1]);
          doc.setDrawColor(0);
          doc.line(5, top, pageWidth - 5, top);
        }

        const isLastTaxRow =
          data.row.section === "body" && data.row.index === taxBody?.length - 1;

        if (isLastTaxRow && data.column.index === 0) {
          const bottom = data.cell.y + data.cell.height + 2;
          doc.setLineDash([1, 1]);
          doc.setDrawColor(0);
          doc.line(5, bottom, pageWidth - 5, bottom);
          doc.setLineDash([]);
        }
      },
    });
    y = doc.lastAutoTable.finalY + 13;
  }
  y = checkPageBreak(doc, y);
  if (parseAmount(documentConfig?.totalTaxAmount) > 0) {
    doc.setFontSize(10);
    doc.text("Total Tax", 5, y);
    doc.text(`${documentConfig?.totalTaxAmount}`, pageWidth - 5, y, { align: "right" });
    y += 8;
  }
  y = checkPageBreak(doc, y);

  const payLabelX = 8; // left label
  const payColonX = 72; // aligned colon
  const payValueX = 78; // value starts just after :

  const drawPaymentRow = (label, amount, isBold = false) => {
    if (parseAmount(amount) > 0) {
      y = checkPageBreak(doc, y);

      doc.setFont(
        isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular",
        isBold ? "bold" : "normal",
      );
      doc.setFontSize(10);

      doc.text(label, payLabelX, y);
      doc.text(":", payColonX, y);
      doc.text(`${amount}`, payValueX, y);

      y += 9;
    }
  };

  // -------- PAYMENT MODE HEADER --------
    if (hasPayment) {
  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(5, y, pageWidth - 5, y);
  doc.setLineDash([]);
  y += 13;

  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(9);
  doc.text("PAYMENT MODE", pageWidth / 2, y, { align: "center" });
  y += 14;

  // -------- PAYMENT VALUES --------
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);

  // Adjust values
  const cashPaid = (data?.cashamount || 0) - (data?.cashchange || 0);

  const cardPaid = (data?.cardamount || 0) - (data?.cardchange || 0);

  const onlinePaid = (data?.onlineamount || 0) - (data?.onlinechange || 0);

  const upiPaid = (data?.upiamount || 0) - (data?.upichange || 0);

  const balanceDue = (data?.paylateramount || 0) - (data?.paylaterchange || 0);

  // Draw rows only if > 0
  drawPaymentRow("Cash", documentConfig?.cashPaid);
  drawPaymentRow("Card", documentConfig?.cardPaid);
  drawPaymentRow("Online Pay", documentConfig?.onlinePaid);
  drawPaymentRow("UPI", documentConfig?.upiPaid);

  // -------- BALANCE DUE --------
  if (parseAmount(documentConfig?.balanceDue) > 0) {
    // y += 6;
    drawPaymentRow("Balance Due", documentConfig?.balanceDue, true);
  }
    }
  // -------- FOOTER LINE --------
  y -= 4;
  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(5, y, pageWidth - 5, y);
  doc.setLineDash([]);
  y += 12;
  doc.text("Thank you for your purchase!", pageWidth / 2, y, {
    align: "center",
  });
  y += 12;
  doc.setFont("NotoSansMono-Bold", "bold");
  doc.text("** Visit us Again **", pageWidth / 2, y, { align: "center" });
  if (storeSettings?.negobillBranding) {
    y += 11;
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text("Powered By", pageWidth / 2, y, { align: "center" });
    y += 11;
    doc.setFontSize(9);
    const sitePrefix = "www.";
    const siteBold = "NegoBill";
    const siteSuffix = ".com";
    const prefixWidth = doc.getTextWidth(sitePrefix);
    const boldWidth = doc.getTextWidth(siteBold);
    const suffixWidth = doc.getTextWidth(siteSuffix);
    const totalWidth = prefixWidth + boldWidth + suffixWidth;
    const startX = (pageWidth - totalWidth) / 2;

    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(sitePrefix, startX, y);
    doc.setFont("NotoSansMono-Bold", "bold");
    doc.text(siteBold, startX + prefixWidth, y);
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(siteSuffix, startX + prefixWidth + boldWidth, y);
    y += 20;
  }
  // setPrint(false);
  return doc;
};

export const Browser3InchBillReceipt = async (data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings) => {
   const {
    documentConfig,hasPayment       
  } = invoiceConfig;
  const mm = (pt) => pt / 2.83465;
  const taxRows1 =documentConfig?.items;
  const itemHeight =documentConfig?.items?.length * mm(45); // Approx 45pt per item row
  const taxHeight = Object.keys(taxRows1)?.length * mm(25); //25
  const footerHeight = mm(250); // Total for branding, thank you, and payment boxes
  const headerHeight = mm(250); // Total for logo, store info, and invoice details

  // Total dynamic height with a small buffer
  const dynamicHeight = headerHeight + itemHeight + taxHeight + footerHeight;
  const doc = new jsPDF({
    unit: "pt",
    format: [204, 1000],
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 16;

  const isIGST =
    documentConfig?.fromData?.statecode &&
    documentConfig?.toData?.statecode &&
    documentConfig?.fromData.statecode !== documentConfig?.toData?.statecode;

  const taxRows =documentConfig?.items?.reduce((acc, product) => {
    const taxPercentage = product?.taxpercentage || 0;
    const taxAmount = product?.lineTotalTaxAmount || 0;
    const basePrice = product?.lineTotalBasePrice || 0;
    if (!acc[taxPercentage]) {
      acc[taxPercentage] = { basePrice: 0, sgst: 0, cgst: 0, igst: 0 };
    }
    acc[taxPercentage].basePrice += basePrice;
    if (isIGST) {
      acc[taxPercentage].igst += taxAmount;
    } else {
      acc[taxPercentage].sgst += taxAmount / 2;
      acc[taxPercentage].cgst += taxAmount / 2;
    }
    return acc;
  }, {});

  const checkPageBreak = (doc, y, buffer = 20) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + buffer >= pageHeight) {
      doc.addPage();
      return 14; // reset y to top margin for new page
    }
    return y;
  };

  const imagePath = data?.imagePath;

  if (documentConfig?.image) {
    try {
      // Step 1: Convert image URL to base64
      const response = await fetch(imagePath, { mode: "cors" });
      const blob = await response.blob();
      let base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Step 2: Convert to grayscale
      base64 = await convertToGrayScale(base64);

      // Step 3: Get natural dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Step 4: Resize to fit dynamically within maxWidth and maxHeight
      const maxWidth = 50; // maximum width in pt for 3-inch receipt
      const maxHeight = 40; // maximum height in pt
      const widthRatio = maxWidth / naturalWidth;
      const heightRatio = maxHeight / naturalHeight;
      const ratio = Math.min(widthRatio, heightRatio, 1); // don't upscale

      const imgWidth = naturalWidth * ratio;
      const imgHeight = naturalHeight * ratio;

      // Step 5: Center the logo
      const logoX = (pageWidth - imgWidth) / 2;

      doc.addImage(base64, "PNG", logoX, y, imgWidth, imgHeight);

      y += imgHeight + 10; // spacing after logo
    } catch (err) {
      console.warn("Logo failed to load:", err);
    }
  }

  const invoiceTitle =
    data?.status === "CANCELLED"
      ? "CANCELLED TAX INVOICE"
      :documentConfig?.title;

  doc
    .setFont("NotoSansMono-Bold", "bold")
    .setFontSize(10)
    .text(invoiceTitle, pageWidth / 2, y, { align: "center" });
  y += 13;

  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(12);
  const storeTitle = documentConfig?.fromData?.title || "Store";
  const wrappedStoreTitle = doc.splitTextToSize(storeTitle, pageWidth - 20);
  wrappedStoreTitle.forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += 10;
  });

  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);
  if (documentConfig?.fromData?.phone || documentConfig?.fromData?.additionalPhone) {
    doc.text(`Ph:${documentConfig?.fromData?.phone
            ? `${documentConfig.fromData.phone}`
            : ""
        }${
          documentConfig?.fromData?.additionalPhone
            ? `, ${documentConfig.fromData.additionalPhone}`
            : ""
        } `, pageWidth / 2, y, {
      align: "center",
    });
    y += 9;
  }

  const seller = documentConfig?.fromData;
  const sellerAddressParts = [
    seller?.addressline1,
    [seller?.state, seller?.district, seller?.country]
      .filter(Boolean)
      .join(", "),
    seller?.pincode ? `- ${seller?.pincode}` : null,
  ].filter(Boolean);

  doc
    .splitTextToSize(sellerAddressParts.join(", "), pageWidth - 20)
    .forEach((line) => {
      doc.text(line, pageWidth / 2, y, { align: "center" });
      y += 10;
    });

  if (seller?.email) {
    doc.text(`Email: ${seller?.email}`, pageWidth / 2, y, { align: "center" });
    y += 10;
  }
  // doc.text(`GST: ${seller?.gstnumber || ""}`, pageWidth / 2, y, {
  //   align: "center",
  // });
  // y += (6);
  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(5, y, pageWidth - 5, y);
  doc.setLineDash([]);
  y += 12;

  const { date, time } = documentConfig?.Date
  const labelX = 5; // Left-aligned label start
  const colonX = 65; // Colon aligned at this point
  const valueX = 70; // Value starts after colon
  const lineHeight = 8;
  const drawRowWithColonAligned = (label, value, yPos, isBold = false) => {
    const font = isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular";
    doc.setFont(font, isBold ? "bold" : "normal").setFontSize(9);
    doc.text(label, labelX, yPos);
    doc.text(":", colonX, yPos);
    doc.text(value, valueX, yPos);
  };
  drawRowWithColonAligned(documentConfig?.numberLabel, `${documentConfig?.number}`, y);
  y += lineHeight;
  drawRowWithColonAligned("Date", `${date}, ${time}`, y);
  y += lineHeight;
  drawRowWithColonAligned("Amount", `${documentConfig?.grandTotal}`, y, true);
  y += 6;
  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(5, y, pageWidth - 5, y);
  y += 11;

  const buyer = documentConfig?.toData;
  const hasBuyerInfo =
    buyer &&
    (buyer?.name ||
      buyer?.phone ||
      buyer?.addressline1 ||
      buyer?.email ||
      buyer?.gstnumber ||
      buyer?.pannumber ||
      buyer?.cinnumber);

  if (hasBuyerInfo) {
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);
    doc.text("BUYER INFORMATION", pageWidth / 2, y, { align: "center" });
    y += 9;

    if (buyer?.name) {
      doc.text(`${buyer?.name}`, 10, y);
      y += 8;
    }
    if (buyer?.phone) {
      doc.text(`Ph: ${buyer?.phone}`, 10, y);
      y += 8;
    }

    const addressParts = [
      buyer?.addressline1,
      [buyer?.state, buyer?.district, buyer?.country]
        .filter(Boolean)
        .join(", "),
      buyer?.pincode ? `- ${buyer?.pincode}` : null,
    ].filter(Boolean);

    doc
      .splitTextToSize(addressParts.join(", "), pageWidth - 20)
      .forEach((line) => {
        doc.text(line, 10, y);
        y += 8;
      });

    if (buyer?.email) {
      doc.text(`Email: ${buyer?.email}`, 10, y);
      y += 8;
    }
    if (buyer?.gstnumber) {
      doc.text(`GST: ${buyer?.gstnumber}`, 10, y);
      y += 8;
    }
    if (buyer?.pannumber) {
      doc.text(`PAN: ${buyer?.pannumber}`, 10, y);
      y += 8;
    }
    if (buyer?.cinnumber) {
      doc.text(`CIN: ${buyer?.cinnumber}`, 10, y);
      y += 8;
    }
    if (buyer?.fssai) {
      doc.text(`FSSI: ${buyer?.fssai}`, 10, y);
      y += 8;
    }
    if (buyer?.referenceid) {
      doc.text(`Reference No: ${buyer?.referenceid}`, 10, y);
      y += 5;
    }    
           
    doc.setDrawColor(0);
    doc.setLineDash([1, 1]);
    doc.line(5, y, pageWidth - 5, y);
    doc.setLineDash([]);
    y += 4;
  }
  const itemRows = [
    [
      {
        content: "",
        colSpan: 4,
        styles: {
          fontSize: 2,
          lineWidth: 0,
          halign: "center",
        },
      },
    ],
    // Actual item rows
    ...documentConfig?.items.flatMap((item) => {
      const rows = [];

      // Title row
      rows.push([
        {
          content: item?.title,
          colSpan: 4,
          styles: {
            fontSize: 9,
            fontStyle: "normal",
            font: "NotoSansMono-Regular",
          },
        },
      ]);

      // Note row (only if present and imode = PRODUCT)
      // if (item.imode === "PRODUCT" && item.note) {
      //   rows.push([
      //     {
      //       content: `(${item.note})`,
      //       colSpan: 4,
      //       styles: {
      //         fontSize: 5,
      //         textColor: [100, 100, 100],
      //         fontStyle: "normal",
      //       },
      //     },
      //   ]);
      // }

      // Price row
      rows.push([
        "",
        `${item?.soldprice}`,
        `${item?.quantity} ${(() => {
          const unitCode = item?.isSecondaryUnit
            ? item?.unitConfig?.secondaryUnitCode
            : item?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`,
        `${item?.lineTotalSoldPrice}`,
      ]);

      return rows;
    }),
  ];

  autoTable(doc, {
    startY: y,
    styles: {
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
    },
    head: [
      [
        {
          content: "Item",
          colSpan: 4,
          styles: {
            halign: "left",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
      [
        { content: "", styles: { cellPadding: 0 } },
        {
          content: "Rate",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Qty",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Total",
          styles: {
            halign: "right",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
    ],
    body: itemRows,
    theme: "plain",
    styles: {
      fontSize: 9,
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
      lineWidth: 0,
      font: "NotoSansMono-Regular",
      fontStyle: "normal",
    },
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "center" },
      2: { halign: "center" },
      3: { halign: "right" },
    },
    margin: { left: 0, right: 5 },
    didDrawCell: (data) => {
      const pageWidth = doc.internal.pageSize.getWidth();
      const left = 0;
      const right = pageWidth - 5;
      // Bottom dotted line after the second header row ("Rate Qty Total")
      if (
        data.row.section === "head" &&
        data.row.index === 1 &&
        data.column.index === 3
      ) {
        const bottom = data.cell.y + data.cell.height + 2;
        doc.setLineDash([1, 1]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        // doc.setLineDash([]);
      }
      // Bottom dotted line after last row
      if (
        data.row.section === "body" &&
        data.row.index === itemRows?.length - 1 &&
        data.column.index === 0
      ) {
        const bottom = data.cell.y + data.cell.height + 2;
        doc.setLineDash([1, 1]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        doc.setLineDash([]);
      }
    },
  });
  y = doc.lastAutoTable.finalY + 10;
  y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(8);
  doc.text(`Total Item: ${data?.totalItems}`, 0, y);
  y += 10;
  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(10);
  doc.text(`Grand Total: ${documentConfig?.grandTotal}`, 0, y, { align: "left" });
  y += 5;
  // const itemRows = [
  //   [
  //     {
  //       content: "",
  //       colSpan: 4,
  //       styles: {
  //         fontSize: 9,
  //         // cellPadding: 2,
  //         lineWidth: 0,
  //         halign: "center",
  //       },
  //     },
  //   ],
  //   ...data.saleitems.flatMap((item) => {
  //     const rows = [];

  //     // Title + Note in single line
  //     let titleWithNote = item.title;
  //     if (item.imode === "PRODUCT" && item.note) {
  //       titleWithNote += ` (${item.note})`;
  //     }

  //     rows.push([
  //       {
  //         content: titleWithNote,
  //         colSpan: 4,
  //       },
  //     ]);

  //     // Price row
  //     rows.push([
  //       "",
  //       `${CurrencyConverter.toCurrency(item.soldprice || 0, "INR")}`,
  //       `${item.quantity}`,
  //       `${CurrencyConverter.toCurrency(
  //         item.lineTotalSoldPrice || 0,
  //         "INR",
  //       )}`,
  //     ]);

  //     return rows;
  //   }),
  // ];

  // autoTable(doc, {
  //   startY: y,
  //   pageBreak: "auto",
  //   styles:{
  //     cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
  //   },
  //   head: [
  //     [
  //       {
  //         content: "Item",
  //         colSpan: 4,
  //         styles: { halign: "left", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //     ],
  //     [
  //       { content: "", styles: { cellPadding: 0 } },
  //       {
  //         content: "Rate",
  //         styles: { halign: "center", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //       {
  //         content: "Qty",
  //         styles: { halign: "center", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //       {
  //         content: "Total",
  //         styles: { halign: "right", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //     ],
  //   ],
  //   body: itemRows,
  //   theme: "plain",
  //   styles: {
  //     fontSize: 12,
  //     styles:{
  //       cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
  //     },
  //     lineWidth: 0,
  //     font: "NotoSansMono-Regular",
  //     fontStyle: "normal",
  //   },
  //   columnStyles: {
  //     0: { halign: "left" },
  //     1: { halign: "center" },
  //     2: { halign: "center" },
  //     3: { halign: "right" },
  //   },
  //   margin: { left: (5), right: (5) },
  //   didDrawCell: (data) => {
  //     const pageWidth = doc.internal.pageSize.getWidth();
  //     const left = (5);
  //     const right = pageWidth - (5);
  //     // Bottom dotted line after the second header row ("Rate Qty Total")
  //     if (
  //       data.row.section === "head" &&
  //       data.row.index === 1 &&
  //       data.column.index === 3
  //     ) {
  //       const bottom = data.cell.y + data.cell.height + 2;
  //       doc.setDrawColor(0);
  //       doc.setLineDash([(1), (1)]);
  //       doc.line(left, bottom, right, bottom);
  //       doc.setLineDash([]);
  //     }
  //     // Bottom dotted line after last row
  //     if (
  //       data.row.section === "body" &&
  //       data.row.index === itemRows.length - 1 &&
  //       data.column.index === 0
  //     ) {
  //       const bottom = data.cell.y + data.cell.height + (2);
  //       doc.setDrawColor(0);
  //       doc.setLineDash([(1), (1)]);
  //       doc.line(left, bottom, right, bottom);
  //       doc.setLineDash([]);
  //     }
  //   },
  // });

  // y = doc.lastAutoTable.finalY + (20);
  // // y = checkPageBreak(doc, y);
  // doc.setFont("NotoSansMono-Regular", "normal");
  // doc.setFontSize(10);
  // doc.text(`Total Item: ${data.totalItems}`, (5), y);
  // y += (15);

  // doc.setFont("NotoSansMono-Bold", "bold").setFontSize(14);
  // doc.text(
  //   `Total Amount: ${CurrencyConverter.toCurrency(
  //     data.grandtotal || 0,
  //     "INR",
  //   )}.00`,
  //   (5),
  //   y,
  // );
  // y += (12);

  doc.setLineDash([1, 1]);
  doc.setDrawColor(0);
  doc.line(7, y, pageWidth - 7, y);
  doc.setLineDash([]);
  y += 11;

  y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(10);
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text("**** YOU SAVED ****", pageWidth / 2, y, { align: "center" });
    y += 10;
  }
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text(`${documentConfig?.totalDiscount}`, pageWidth / 2, y, { align: "center" });
  }
  y += 6;
  // y = checkPageBreak(doc, y);
  // TAX SUMMARY
  // const taxHead = isIGST
  //   ? [["Tax", "Amount", "IGST"]]
  //   : [["Tax", "Amount", "SGST", "CGST"]];

  // const taxBody = Object.entries(taxRows)
  //   .filter(([percent]) => parseFloat(percent) > 0)
  //   .map(([percent, t]) =>
  //     isIGST
  //       ? [
  //           `${percent}%`,
  //           `${CurrencyConverter.toCurrency(t.basePrice || 0, "INR")}`,
  //           `${CurrencyConverter.toCurrency(t.igst || 0, "INR")}`,
  //         ]
  //       : [
  //           `${percent}%`,
  //           `${CurrencyConverter.toCurrency(t.basePrice || 0, "INR")}`,
  //           `${CurrencyConverter.toCurrency(t.sgst || 0, "INR")}`,
  //           `${CurrencyConverter.toCurrency(t.cgst || 0, "INR")}`,
  //         ],
  //   );

  // if (taxBody.length > 0) {
  //   autoTable(doc, {
  //     startY: y,
  //     pageBreak: "auto",
  //     head: [
  //       taxHead[0].map((cell, colIndex) => ({
  //         content: cell,
  //         styles: {
  //           font: "NotoSansMono-Regular",
  //           fontStyle: "normal",
  //           fontSize: 8,
  //           halign: colIndex === 0 ? "left" : "right",
  //         },
  //       })),
  //     ],
  //     body: taxBody.map((row) =>
  //       row.map((cell, colIndex) => ({
  //         content: cell,
  //         styles: {
  //           font: "NotoSansMono-Regular",
  //           fontStyle: "normal",
  //           fontSize: 8,
  //           halign: colIndex === 0 ? "left" : "right",
  //         },
  //       })),
  //     ),
  //     theme: "plain",
  //     styles: {
  //       font: "NotoSansMono-Regular",
  //       fontStyle: "normal",
  //       fontSize: 8,
  //       cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
  //       lineWidth: 0,
  //     },
  //     margin: { left: (5), right: (5) },
  //     didDrawCell: (data) => {
  //       const pageWidth = doc.internal.pageSize.getWidth();

  //       if (
  //         data.row.section === "head" &&
  //         data.row.index === 0 &&
  //         data.column.index === 0
  //       ) {
  //         const top = data.cell.y - 2;
  //        doc.setLineDash([(1), (1)]);
  //         doc.setDrawColor(0);
  //         doc.line((5), top, pageWidth - (5), top);
  //       }

  //       const isLastTaxRow =
  //         data.row.section === "body" &&
  //         data.row.index === taxBody.length - 1;

  //       if (isLastTaxRow && data.column.index === 0) {
  //         const bottom = data.cell.y + data.cell.height + (2);
  //        doc.setLineDash([(1), (1)]);
  //         doc.setDrawColor(0);
  //         doc.line((5), bottom, pageWidth - (5), bottom);
  //         doc.setLineDash([]);
  //       }
  //     },
  //   });
  //   y = doc.lastAutoTable.finalY + (13);
  // }
  // // y = checkPageBreak(doc, y);
  // if (parseFloat(data.totaltaxamount) > 0) {
  //   doc.setFontSize(9);
  //   doc.text("Total Tax", (5), y);
  //   doc.text(
  //     `${CurrencyConverter.toCurrency(data.totaltaxamount || 0, "INR")}`,
  //     pageWidth - (5),
  //     y,
  //     { align: "right" },
  //   );
  //   y += (8);
  // }
  // y = checkPageBreak(doc, y);

  const payLabelX = 8; // left label
  const payColonX = 72; // aligned colon
  const payValueX = 78; // value starts just after :

  const drawPaymentRow = (label, amount, isBold = false) => {
    if (parseAmount(amount) > 0) {
      y = checkPageBreak(doc, y);

      doc.setFont(
        isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular",
        isBold ? "bold" : "normal",
      );
      doc.setFontSize(9);

      doc.text(label, payLabelX, y);
      doc.text(":", payColonX, y);
      doc.text(`${amount}`, payValueX, y);

      y += 9;
    }
  };

  // -------- PAYMENT MODE HEADER --------
 if (hasPayment) {
  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(5, y, pageWidth - 5, y);
  doc.setLineDash([]);
  y += 13;

  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(9);
  doc.text("PAYMENT MODE", pageWidth / 2, y, { align: "center" });
  y += 14;

  // -------- PAYMENT VALUES --------
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);

  // Adjust values
  const cashPaid = (data?.cashamount || 0) - (data?.cashchange || 0);

  const cardPaid = (data?.cardamount || 0) - (data?.cardchange || 0);

  const onlinePaid = (data?.onlineamount || 0) - (data?.onlinechange || 0);

  const upiPaid = (data?.upiamount || 0) - (data?.upichange || 0);

  const balanceDue = (data?.paylateramount || 0) - (data?.paylaterchange || 0);

  // Draw rows only if > 0
  drawPaymentRow("Cash", documentConfig?.cashPaid);
  drawPaymentRow("Card", documentConfig?.cardPaid);
  drawPaymentRow("Online Pay", documentConfig?.onlinePaid);
  drawPaymentRow("UPI", documentConfig?.upiPaid);

  // -------- BALANCE DUE --------
  if (parseAmount(documentConfig?.balanceDue) > 0) {
    // y += 6;
    drawPaymentRow("Balance Due", documentConfig?.balanceDue, true);
  }
 }
  // -------- FOOTER LINE --------
  y -= 4;
  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(5, y, pageWidth - 5, y);
  doc.setLineDash([]);
  y += 12;
  doc.text("Thank you for your purchase!", pageWidth / 2, y, {
    align: "center",
  });
  y += 12;
  doc.setFont("NotoSansMono-Bold", "bold");
  doc.text("** Visit us Again **", pageWidth / 2, y, { align: "center" });
  if (storeSettings?.negobillBranding) {
    y += 11;
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text("Powered By", pageWidth / 2, y, { align: "center" });
    y += 11;
    doc.setFontSize(9);
    const sitePrefix = "www.";
    const siteBold = "NegoBill";
    const siteSuffix = ".com";
    const prefixWidth = doc.getTextWidth(sitePrefix);
    const boldWidth = doc.getTextWidth(siteBold);
    const suffixWidth = doc.getTextWidth(siteSuffix);
    const totalWidth = prefixWidth + boldWidth + suffixWidth;
    const startX = (pageWidth - totalWidth) / 2;

    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(sitePrefix, startX, y);
    doc.setFont("NotoSansMono-Bold", "bold");
    doc.text(siteBold, startX + prefixWidth, y);
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(siteSuffix, startX + prefixWidth + boldWidth, y);
    y += 20;
  }
  // setPrint(false);
  return doc;
};

export const Browser4InchInvoiceReceipt = async (
 data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings
) => { 
  const {
    documentConfig, hasPayment      
  } = invoiceConfig;
  const mm = (pt) => pt / 2.83465;
  const taxRows1 =documentConfig?.items;
  const itemHeight =documentConfig?.items?.length * mm(65); // Approx 45pt per item row
  const taxHeight = Object.keys(taxRows1)?.length * mm(55);
  const footerHeight = mm(350); // Total for branding, thank you, and payment boxes
  const headerHeight = mm(350); // Total for logo, store info, and invoice details

  // Total dynamic height with a small buffer
  const dynamicHeight = headerHeight + itemHeight + taxHeight + footerHeight;

  const doc = new jsPDF({
    unit: "pt",
    format: [288, 1000],
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 18;

  const isIGST =
    documentConfig?.fromData?.statecode &&
    documentConfig?.toData?.statecode &&
    documentConfig?.fromData.statecode !== documentConfig?.toData?.statecode;

  const taxRows =documentConfig?.items?.reduce((acc, product) => {
    const taxPercentage = product?.taxpercentage || 0;
    const taxAmount = product?.lineTotalTaxAmount || 0;
    const basePrice = product?.lineTotalBasePrice || 0;
    if (!acc[taxPercentage]) {
      acc[taxPercentage] = { basePrice: 0, sgst: 0, cgst: 0, igst: 0 };
    }
    acc[taxPercentage].basePrice += basePrice;
    if (isIGST) {
      acc[taxPercentage].igst += taxAmount;
    } else {
      acc[taxPercentage].sgst += taxAmount / 2;
      acc[taxPercentage].cgst += taxAmount / 2;
    }
    return acc;
  }, {});
  const checkPageBreak = (doc, y, buffer = 20) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + buffer >= pageHeight) {
      doc.addPage();
      return 14;
    }
    return y;
  };

 const imagePath =  documentConfig?.image;

  if (documentConfig?.image) {
    try {
      // Step 1: Convert image URL to base64
      const response = await fetch(imagePath, { mode: "cors" });
      const blob = await response.blob();
      let base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Step 2: Convert to grayscale
      base64 = await convertToGrayScale(base64);

      // Step 3: Get natural dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Step 4: Resize to fit dynamically within maxWidth and maxHeight
      const maxWidth = 80; // maximum width in pt for 3-inch receipt
      const maxHeight = 40; // maximum height in pt
      const widthRatio = maxWidth / naturalWidth;
      const heightRatio = maxHeight / naturalHeight;
      const ratio = Math.min(widthRatio, heightRatio, 1); // don't upscale

      const imgWidth = naturalWidth * ratio;
      const imgHeight = naturalHeight * ratio;

      // Step 5: Center the logo
      const logoX = (pageWidth - imgWidth) / 2;

      doc.addImage(base64, "PNG", logoX, y, imgWidth, imgHeight);

      y += imgHeight + 20; // spacing after logo
    } catch (err) {
      console.warn("Logo failed to load:", err);
    }
  }

  // Header
   const invoiceTitle =
    data?.status === "CANCELLED"
      ? "CANCELLED TAX INVOICE"
      :documentConfig?.title;


  doc
    .setFont("NotoSansMono-Bold", "bold")
    .setFontSize(12)
    .text(invoiceTitle, pageWidth / 2, y, { align: "center" });
  y += 13;

  doc.setFontSize(14);
  const storeTitle = documentConfig?.fromData?.title || "Store";
  doc.splitTextToSize(storeTitle, pageWidth - 30).forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += 13;
  });

  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(10);
  if (documentConfig?.fromData?.phone || documentConfig?.fromData?.additionalPhone) {
    doc.text(`Ph: ${documentConfig?.fromData?.phone
            ? `${documentConfig.fromData.phone}`
            : ""
        }${
          documentConfig?.fromData?.additionalPhone
            ? `, ${documentConfig.fromData.additionalPhone}`
            : ""
        }`, pageWidth / 2, y, {
      align: "center",
    });
    y += 12;
  }

  const seller = documentConfig?.fromData;
  const sellerAddressParts = [
    seller?.addressline1,
    [seller?.state, seller?.district, seller?.country]
      .filter(Boolean)
      .join(", "),
    seller?.pincode ? `- ${seller?.pincode}` : null,
  ].filter(Boolean);

  doc
    .splitTextToSize(sellerAddressParts.join(", "), pageWidth - 30)
    .forEach((line) => {
      doc.text(line, pageWidth / 2, y, { align: "center" });
      y += 11;
    });

  if (seller?.email) {
    doc.text(`Email: ${seller?.email}`, pageWidth / 2, y, { align: "center" });
    y += 11;
  }
  doc.text(`GST: ${seller?.gstnumber || ""}`, pageWidth / 2, y, {
    align: "center",
  });
  y += 5;

  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(10, y, pageWidth - 10, y);
  doc.setLineDash([]);
  y += 10;

  const { date, time } = documentConfig?.Date
  const labelX = 8; // Left-aligned label start
  const colonX = 110; // Colon aligned at this point
  const valueX = 120; // Value starts after colon
  const lineHeight = 10;
  const drawRowWithColonAligned = (label, value, yPos, isBold = false) => {
    const font = isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular";
    doc.setFont(font, isBold ? "bold" : "normal").setFontSize(10);
    doc.text(label, labelX, yPos);
    doc.text(":", colonX, yPos);
    doc.text(value, valueX, yPos);
  };
  drawRowWithColonAligned(
    documentConfig?.numberLabel ,
    `${documentConfig?.number}`,
    y,
  );
  y += lineHeight;
  drawRowWithColonAligned("Date", `${date}, ${time}`, y);
  y += lineHeight;
  drawRowWithColonAligned("Total Amount", `${documentConfig?.grandTotal}`, y, true);
  y += 7;

  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(10, y, pageWidth - 10, y);
  y += 10;

  const buyer = documentConfig?.toData;
  const hasBuyerInfo =
    buyer &&
    (buyer?.name ||
      buyer?.phone ||
      buyer?.addressline1 ||
      buyer?.email ||
      buyer?.gstnumber ||
      buyer?.pannumber ||
      buyer?.cinnumber);

  if (hasBuyerInfo) {
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(10);
    doc.text("BUYER INFORMATION", pageWidth / 2, y, { align: "center" });
    y += 11;

    if (buyer?.name) {
      doc.text(`${buyer?.name}`, 10, y);
      y += 11;
    }
    if (buyer?.phone) {
      doc.text(`Ph: ${buyer?.phone}`, 10, y);
      y += 11;
    }

    const addressParts = [
      buyer?.addressline1,
      [buyer?.state, buyer?.district, buyer?.country]
        .filter(Boolean)
        .join(", "),
      buyer?.pincode ? `- ${buyer?.pincode}` : null,
    ].filter(Boolean);

    doc
      .splitTextToSize(addressParts.join(", "), pageWidth - 20)
      .forEach((line) => {
        doc.text(line, 11, y);
        y += 11;
      });

    if (buyer?.email) {
      doc.text(`Email: ${buyer?.email}`, 10, y);
      y += 11;
    }
    if (buyer?.gstnumber) {
      doc.text(`GST: ${buyer?.gstnumber}`, 10, y);
      y += 11;
    }
    if (buyer?.pannumber) {
      doc.text(`PAN: ${buyer?.pannumber}`, 10, y);
      y += 11;
    }
    if (buyer?.cinnumber) {
      doc.text(`CIN: ${buyer?.cinnumber}`, 10, y);
      y += 11;
    }
    if (buyer?.fssai) {
      doc.text(`FSSAI: ${buyer?.fssai}`, 10, y);
      y += 11;
    }
    if (buyer?.referenceid) {
      doc.text(`Reference No: ${buyer?.referenceid}`, 10, y);
      y += 11;
    }   
  }
  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(10, y, pageWidth - 10, y);
  doc.setLineDash([]);
  y += 9;
  const itemRows = [
    [
      {
        content: "",
        colSpan: 4,
        styles: {
          fontSize: 2,
          lineWidth: 0,
          halign: "center",
        },
      },
    ],
    // Actual item rows
    ...documentConfig?.items.flatMap((item) => {
      const rows = [];

      // Title row
      rows.push([
        {
          content: item?.title,
          colSpan: 4,
          styles: {
            fontSize: 10,
            fontStyle: "normal",
            font: "NotoSansMono-Regular",
          },
        },
      ]);

      // Note row (only if present and imode = PRODUCT)
      // if (item.imode === "PRODUCT" && item.note) {
      //   rows.push([
      //     {
      //       content: `(${item.note})`,
      //       colSpan: 4,
      //       styles: {
      //         fontSize: 5,
      //         textColor: [100, 100, 100],
      //         fontStyle: "normal",
      //       },
      //     },
      //   ]);
      // }

      // Price row
      rows.push([
        "",
        `${item?.soldprice}`,
        `${item?.quantity} ${(() => {
          const unitCode = item?.isSecondaryUnit
            ? item?.unitConfig?.secondaryUnitCode
            : item?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`,
        `${item.lineTotalSoldPrice}`,
      ]);

      return rows;
    }),
  ];

  autoTable(doc, {
    startY: y,
    styles: {
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
    },
    head: [
      [
        {
          content: "Item",
          colSpan: 4,
          styles: {
            halign: "left",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
      [
        { content: "", styles: { cellPadding: 0 } },
        {
          content: "Rate",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Qty",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Total",
          styles: {
            halign: "right",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
    ],
    body: itemRows,
    theme: "plain",
    styles: {
      fontSize: 10,
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
      lineWidth: 0,
      font: "NotoSansMono-Regular",
      fontStyle: "normal",
    },
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "center" },
      2: { halign: "center" },
      3: { halign: "right" },
    },
    margin: { left: 0, right: 5 },
    didDrawCell: (data) => {
      const pageWidth = doc.internal.pageSize.getWidth();
      const left = 0;
      const right = pageWidth - 5;
      // Bottom dotted line after the second header row ("Rate Qty Total")
      if (
        data.row.section === "head" &&
        data.row.index === 1 &&
        data.column.index === 3
      ) {
        const bottom = data.cell.y + data.cell.height + 2;
        doc.setLineDash([1, 1]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        // doc.setLineDash([]);
      }
      // Bottom dotted line after last row
      if (
        data.row.section === "body" &&
        data.row.index === itemRows?.length - 1 &&
        data.column.index === 0
      ) {
        const bottom = data.cell.y + data.cell.height + 2;
        doc.setLineDash([1, 1]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        doc.setLineDash([]);
      }
    },
  });
  y = doc.lastAutoTable.finalY + 11;
  y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(10);
  doc.text(`Total Item: ${data?.totalItems}`, 0, y);
  y += 10;
  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(11);
  doc.text(`Grand Total: ${documentConfig?.grandTotal}`, 0, y, { align: "left" });
  y += 5;
  // const itemRows = [
  //   [
  //     {
  //       content: "",
  //       colSpan: 4,
  //       styles: {
  //         fontSize: 9,
  //         cellPadding: 2,
  //         lineWidth: 0,
  //         halign: "center",
  //       },
  //     },
  //   ],
  //   ...data.saleitems.flatMap((item) => {
  //     const rows = [];

  //     // Title + Note in single line
  //     let titleWithNote = item.title;
  //     // if (item.imode === "PRODUCT" && item.note) {
  //     //   titleWithNote += ` (${item.note})`;
  //     // }

  //     rows.push([
  //       {
  //         content: titleWithNote,
  //         colSpan: 4,
  //       },
  //     ]);

  //     // Price row
  //     rows.push([
  //       "",
  //       `${CurrencyConverter.toCurrency(item.soldprice || 0, "INR")}`,
  //       `${item.quantity}`,
  //       `${CurrencyConverter.toCurrency(
  //         item.lineTotalSoldPrice || 0,
  //         "INR",
  //       )}`,
  //     ]);

  //     return rows;
  //   }),
  // ];
  // autoTable(doc, {
  //   startY: y,
  //   head: [
  //     [
  //       {
  //         content: "Item",
  //         colSpan: 4,
  //         styles: { halign: "left", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //     ],
  //     [
  //       { content: "", styles: { cellPadding: 0 } },
  //       {
  //         content: "Rate",
  //         styles: { halign: "center", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //       {
  //         content: "Qty",
  //         styles: { halign: "center", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //       {
  //         content: "Total",
  //         styles: { halign: "right", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //     ],
  //   ],
  //   body: itemRows,
  //   theme: "plain",
  //   styles: {
  //     fontSize: 14,
  //     cellPadding: 3,
  //     lineWidth: 0,
  //     font: "NotoSansMono-Regular",
  //   },
  //   columnStyles: {
  //     0: { halign: "left" },
  //     1: { halign: "center" },
  //     2: { halign: "center" },
  //     3: { halign: "right" },
  //   },
  //   margin: { left: 8, right: 8 },
  //   didDrawCell: (data) => {
  //     const pageWidth = doc.internal.pageSize.getWidth();
  //     const left = 8;
  //     const right = pageWidth - 8;

  //     if (
  //       data.row.section === "head" &&
  //       data.row.index === 1 &&
  //       data.column.index === 3
  //     ) {
  //       const bottom = data.cell.y + data.cell.height + 2;
  //       doc.setLineDash([1, 1], 0);
  //       doc.setDrawColor(0);
  //       doc.line(left, bottom, right, bottom);
  //       doc.setLineDash([]);
  //     }
  //     if (
  //       data.row.section === "body" &&
  //       data.row.index === itemRows.length - 1 &&
  //       data.column.index === 0
  //     ) {
  //       const bottom = data.cell.y + data.cell.height + 2;
  //       doc.setLineDash([1, 1], 0);
  //       doc.setDrawColor(0);
  //       doc.line(left, bottom, right, bottom);
  //       doc.setLineDash([]);
  //     }
  //   },
  // });

  // y = doc.lastAutoTable.finalY + 18;
  // // y = checkPageBreak(doc, y);
  // doc.setFontSize(14);
  // doc.text(`Total Item: ${data.totalItems}`, 8, y);
  // y += 24;
  // doc.setFont("NotoSansMono-Bold", "bold").setFontSize(18);
  // doc.text(
  //   `Total Amount: ${CurrencyConverter.toCurrency(
  //     data.grandtotal || 0,
  //     "INR",
  //   )}.00`,
  //   8,
  //   y,
  // );
  // y += 12;

  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(10, y, pageWidth - 10, y);
  y += 10;
  y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(10);
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text("**** YOU SAVED ****", pageWidth / 2, y, { align: "center" });
    y += 10;
  }
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text(`${documentConfig?.totalDiscount}`, pageWidth / 2, y, { align: "center" });
  }
  y += 11;
  y = checkPageBreak(doc, y);
  const taxHead = isIGST
    ? [["Tax", "Amount", "IGST"]]
    : [["Tax", "Amount", "SGST", "CGST"]];

  const taxBody = Object.entries(taxRows)
    .filter(([percent]) => parseFloat(percent) > 0)
    .map(([percent, t]) =>
      isIGST
        ? [`${percent}%`, `${t?.basePrice}`, `${t?.igst}`]
        : [`${percent}%`, `${t?.basePrice}`, `${t?.sgst}`, `${t?.cgst}`],
    );

  if (taxBody.length > 0) {
    autoTable(doc, {
      startY: y,
      pageBreak: "auto",
      head: [
        taxHead[0].map((cell, colIndex) => ({
          content: cell,
          styles: {
            font: "NotoSansMono-Regular",
            fontStyle: "normal",
            fontSize: 10,
            halign: colIndex === 0 ? "left" : "right",
          },
        })),
      ],
      body: taxBody?.map((row) =>
        row.map((cell, colIndex) => ({
          content: cell,
          styles: {
            font: "NotoSansMono-Regular",
            fontStyle: "normal",
            fontSize: 10,
            halign: colIndex === 0 ? "left" : "right",
          },
        })),
      ),
      theme: "plain",
      styles: {
        font: "NotoSansMono-Regular",
        fontStyle: "normal",
        fontSize: 9,
        cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
        lineWidth: 0,
      },
      margin: { left: 5, right: 5 },
      didDrawCell: (data) => {
        const pageWidth = doc.internal.pageSize.getWidth();

        if (
          data.row.section === "head" &&
          data.row.index === 0 &&
          data.column.index === 0
        ) {
          const top = data.cell.y - 2;
          doc.setLineDash([1, 1]);
          doc.setDrawColor(0);
          doc.line(5, top, pageWidth - 5, top);
        }

        const isLastTaxRow =
          data.row.section === "body" && data.row.index === taxBody?.length - 1;

        if (isLastTaxRow && data.column.index === 0) {
          const bottom = data.cell.y + data.cell.height + 2;
          doc.setLineDash([1, 1]);
          doc.setDrawColor(0);
          doc.line(5, bottom, pageWidth - 5, bottom);
          doc.setLineDash([]);
        }
      },
    });
    y = doc.lastAutoTable.finalY + 13;
  }
  y = checkPageBreak(doc, y);
  if (parseAmount(documentConfig?.totalTaxAmount) > 0) {
    doc.setFontSize(10);
    doc.text("Total Tax", 5, y);
    doc.text(`${documentConfig?.totalTaxAmount}`, pageWidth - 5, y, { align: "right" });
    y += 9;
  }
  y = checkPageBreak(doc, y);
  // Final footer

  // y += (20);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(10);

  const payLabelX = 10;
  const payColonX = 125; // same column for all :
  const payValueX = 132; // amount starts just after :

  const drawPaymentRow = (label, amount, isBold = false) => {
    if (parseAmount(amount) > 0) {
      y = checkPageBreak(doc, y);

      doc.setFont(
        isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular",
        isBold ? "bold" : "normal",
      );
      doc.setFontSize(10);

      doc.text(label, payLabelX, y);
      doc.text(":", payColonX, y);
      doc.text(`${(amount)}`, payValueX, y);

      y += 11;
    }
  };

  // ---------- PAYMENT MODE HEADER ----------
  if (hasPayment) {
  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(10, y, pageWidth - 10, y);
  doc.setLineDash([]);
  y += 10;

  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(10);
  doc.text("PAYMENT MODE", pageWidth / 2, y + 1, { align: "center" });
  y += 12;

  // ---------- PAYMENT VALUES ----------
  doc.setFont("NotoSansMono-Regular", "normal");

 
  // Draw only if value > 0
  drawPaymentRow("Cash", documentConfig?.cashPaid);
  drawPaymentRow("Card",documentConfig?.cardPaid);
  drawPaymentRow("Online Pay", documentConfig?.onlinePaid);
  drawPaymentRow("UPI", documentConfig?.upiPaid);

  // ---------- BALANCE DUE ----------
  if (parseAmount(documentConfig?.balanceDue) > 0) {
    // y += 6;
    drawPaymentRow("Balance Due", documentConfig?.balanceDue, true);
  }
  }
  // ---------- FOOTER LINE ----------
  y -= 5;
  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(10, y, pageWidth - 10, y);
  doc.setLineDash([]);
  y += 10;
  doc.setFontSize(10);
  doc.text("Thank you for your purchase!", pageWidth / 2, y, {
    align: "center",
  });
  y += 10;
  doc.setFont("NotoSansMono-Bold", "bold");
  doc.text("** Visit us Again **", pageWidth / 2, y, { align: "center" });
  y += 10;
  doc.setFont("NotoSansMono-Regular", "normal");
  doc.text("Powered By", pageWidth / 2, y, { align: "center" });
  y += 10;
  doc.setFontSize(10);
  const sitePrefix = "www.";
  const siteBold = "NegoBill";
  const siteSuffix = ".com";
  const prefixWidth = doc.getTextWidth(sitePrefix);
  const boldWidth = doc.getTextWidth(siteBold);
  const suffixWidth = doc.getTextWidth(siteSuffix);
  const totalWidth = prefixWidth + boldWidth + suffixWidth;
  const startX = (pageWidth - totalWidth) / 2;
  if (storeSettings?.negobillBranding) {
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(sitePrefix, startX, y);
    doc.setFont("NotoSansMono-Bold", "bold");
    doc.text(siteBold, startX + prefixWidth, y);
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(siteSuffix, startX + prefixWidth + boldWidth, y);
    y += 20;
  }
  // setPrint(false);
  return doc;
};

export const Browser4InchBillReceipt = async (data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings) => {
   const {
    documentConfig,  hasPayment     
  } = invoiceConfig; 
 
  const mm = (pt) => pt / 2.83465;
  const taxRows1 =documentConfig?.items;
  const itemHeight =documentConfig?.items?.length * mm(65); // Approx 45pt per item row
  const taxHeight = Object.keys(taxRows1)?.length * mm(55);
  const footerHeight = mm(350); // Total for branding, thank you, and payment boxes
  const headerHeight = mm(350); // Total for logo, store info, and invoice details

  // Total dynamic height with a small buffer
  const dynamicHeight = headerHeight + itemHeight + taxHeight + footerHeight;

  const doc = new jsPDF({
    unit: "pt",
    format: [288, 1000],
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 18;

  const isIGST =
    documentConfig?.fromData?.statecode &&
    documentConfig?.toData?.statecode &&
    documentConfig?.fromData.statecode !== documentConfig?.toData.statecode;

  const taxRows =documentConfig?.items?.reduce((acc, product) => {
    const taxPercentage = product?.taxpercentage || 0;
    const taxAmount = product?.lineTotalTaxAmount || 0;
    const basePrice = product?.lineTotalBasePrice || 0;
    if (!acc[taxPercentage]) {
      acc[taxPercentage] = { basePrice: 0, sgst: 0, cgst: 0, igst: 0 };
    }
    acc[taxPercentage].basePrice += basePrice;
    if (isIGST) {
      acc[taxPercentage].igst += taxAmount;
    } else {
      acc[taxPercentage].sgst += taxAmount / 2;
      acc[taxPercentage].cgst += taxAmount / 2;
    }
    return acc;
  }, {});
  const checkPageBreak = (doc, y, buffer = 20) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + buffer >= pageHeight) {
      doc.addPage();
      return 14;
    }
    return y;
  };

 const imagePath =  documentConfig?.image;

  if (documentConfig?.image) {
    try {
      // Step 1: Convert image URL to base64
      const response = await fetch(imagePath, { mode: "cors" });
      const blob = await response.blob();
      let base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Step 2: Convert to grayscale
      base64 = await convertToGrayScale(base64);

      // Step 3: Get natural dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Step 4: Resize to fit dynamically within maxWidth and maxHeight
      const maxWidth = 80; // maximum width in pt for 3-inch receipt
      const maxHeight = 40; // maximum height in pt
      const widthRatio = maxWidth / naturalWidth;
      const heightRatio = maxHeight / naturalHeight;
      const ratio = Math.min(widthRatio, heightRatio, 1); // don't upscale

      const imgWidth = naturalWidth * ratio;
      const imgHeight = naturalHeight * ratio;

      // Step 5: Center the logo
      const logoX = (pageWidth - imgWidth) / 2;

      doc.addImage(base64, "PNG", logoX, y, imgWidth, imgHeight);

      y += imgHeight + 20; // spacing after logo
    } catch (err) {
      console.warn("Logo failed to load:", err);
    }
  }

  // Header
  const invoiceTitle =
    data?.status === "CANCELLED"
      ? "CANCELLED TAX INVOICE"
      :documentConfig?.title;

  doc
    .setFont("NotoSansMono-Bold", "bold")
    .setFontSize(12)
    .text(invoiceTitle, pageWidth / 2, y, { align: "center" });
  y += 13;

  doc.setFontSize(14);
  const storeTitle = documentConfig?.fromData?.title || "Store";
  doc.splitTextToSize(storeTitle, pageWidth - 30).forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += 13;
  });

  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(10);
  if (documentConfig?.fromData?.phone || documentConfig?.fromData?.additionalPhone) {
    doc.text(`Ph: ${documentConfig?.fromData?.phone
            ? `${documentConfig.fromData.phone}`
            : ""
        }${
          documentConfig?.fromData?.additionalPhone
            ? `, ${documentConfig.fromData.additionalPhone}`
            : ""
        }`, pageWidth / 2, y, {
      align: "center",
    });
    y += 12;
  }

  const seller = documentConfig?.fromData;
  const sellerAddressParts = [
    seller?.addressline1,
    [seller?.state, seller?.district, seller?.country]
      .filter(Boolean)
      .join(", "),
    seller?.pincode ? `- ${seller?.pincode}` : null,
  ].filter(Boolean);

  doc
    .splitTextToSize(sellerAddressParts.join(", "), pageWidth - 30)
    .forEach((line) => {
      doc.text(line, pageWidth / 2, y, { align: "center" });
      y += 11;
    });

  if (seller?.email) {
    doc.text(`Email: ${seller?.email}`, pageWidth / 2, y, { align: "center" });
    y += 11;
  }
  // doc.text(`GST: ${seller?.gstnumber || ""}`, pageWidth / 2, y, {
  //   align: "center",
  // });
  // y += (5);

  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(10, y, pageWidth - 10, y);
  doc.setLineDash([]);
  y += 10;

  const { date, time } = documentConfig?.Date
  const labelX = 8; // Left-aligned label start
  const colonX = 110; // Colon aligned at this point
  const valueX = 120; // Value starts after colon
  const lineHeight = 10;
  const drawRowWithColonAligned = (label, value, yPos, isBold = false) => {
    const font = isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular";
    doc.setFont(font, isBold ? "bold" : "normal").setFontSize(10);
    doc.text(label, labelX, yPos);
    doc.text(":", colonX, yPos);
    doc.text(value, valueX, yPos);
  };
  drawRowWithColonAligned(documentConfig?.numberLabel, `${documentConfig?.number}`, y);
  y += lineHeight;
  drawRowWithColonAligned("Date", `${date}, ${time}`, y);
  y += lineHeight;
  drawRowWithColonAligned("Total Amount", `${documentConfig?.grandTotal}`, y, true);
  y += 7;

  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(10, y, pageWidth - 10, y);
  y += 10;

  const buyer = documentConfig?.toData;
  const hasBuyerInfo =
    buyer &&
    (buyer?.name ||
      buyer?.phone ||
      buyer?.addressline1 ||
      buyer?.email ||
      buyer?.gstnumber ||
      buyer?.pannumber ||
      buyer?.cinnumber);

  if (hasBuyerInfo) {
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(10);
    doc.text("BUYER INFORMATION", pageWidth / 2, y, { align: "center" });
    y += 11;

    if (buyer?.name) {
      doc.text(`${buyer?.name}`, 10, y);
      y += 11;
    }
    if (buyer?.phone) {
      doc.text(`Ph: ${buyer?.phone}`, 10, y);
      y += 11;
    }

    const addressParts = [
      buyer?.addressline1,
      [buyer?.state, buyer?.district, buyer?.country]
        .filter(Boolean)
        .join(", "),
      buyer?.pincode ? `- ${buyer?.pincode}` : null,
    ].filter(Boolean);

    doc
      .splitTextToSize(addressParts.join(", "), pageWidth - 20)
      .forEach((line) => {
        doc.text(line, 11, y);
        y += 11;
      });

    if (buyer?.email) {
      doc.text(`Email: ${buyer?.email}`, 10, y);
      y += 11;
    }
    if (buyer?.gstnumber) {
      doc.text(`GST: ${buyer?.gstnumber}`, 10, y);
      y += 11;
    }
    if (buyer?.pannumber) {
      doc.text(`PAN: ${buyer?.pannumber}`, 10, y);
      y += 11;
    }
    if (buyer?.cinnumber) {
      doc.text(`CIN: ${buyer?.cinnumber}`, 10, y);
      y += 11;
    }
    if (buyer?.fssai) {
      doc.text(`FSSI: ${buyer?.fssai}`, 10, y);
      y += 11;
    }
    if (buyer?.referenceid) {
      doc.text(`Reference No: ${buyer?.referenceid}`, 10, y);
      y += 11;
    }
  }
  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(10, y, pageWidth - 10, y);
  doc.setLineDash([]);
  y += 9;
  const itemRows = [
    [
      {
        content: "",
        colSpan: 4,
        styles: {
          fontSize: 2,
          lineWidth: 0,
          halign: "center",
        },
      },
    ],
    // Actual item rows
    ...documentConfig?.items.flatMap((item) => {
      const rows = [];

      // Title row
      rows.push([
        {
          content: item?.title,
          colSpan: 4,
          styles: {
            fontSize: 10,
            fontStyle: "normal",
            font: "NotoSansMono-Regular",
          },
        },
      ]);

      // Note row (only if present and imode = PRODUCT)
      // if (item.imode === "PRODUCT" && item.note) {
      //   rows.push([
      //     {
      //       content: `(${item.note})`,
      //       colSpan: 4,
      //       styles: {
      //         fontSize: 5,
      //         textColor: [100, 100, 100],
      //         fontStyle: "normal",
      //       },
      //     },
      //   ]);
      // }

      // Price row
      rows.push([
        "",
        `${item?.soldprice}`,
        `${item?.quantity} ${(() => {
          const unitCode = item?.isSecondaryUnit
            ? item?.unitConfig?.secondaryUnitCode
            : item?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`,
        `${item?.lineTotalSoldPrice}`,
      ]);

      return rows;
    }),
  ];

  autoTable(doc, {
    startY: y,
    styles: {
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
    },
    head: [
      [
        {
          content: "Item",
          colSpan: 4,
          styles: {
            halign: "left",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
      [
        { content: "", styles: { cellPadding: 0 } },
        {
          content: "Rate",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Qty",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Total",
          styles: {
            halign: "right",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
    ],
    body: itemRows,
    theme: "plain",
    styles: {
      fontSize: 10,
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
      lineWidth: 0,
      font: "NotoSansMono-Regular",
      fontStyle: "normal",
    },
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "center" },
      2: { halign: "center" },
      3: { halign: "right" },
    },
    margin: { left: 0, right: 5 },
    didDrawCell: (data) => {
      const pageWidth = doc.internal.pageSize.getWidth();
      const left = 0;
      const right = pageWidth - 5;
      // Bottom dotted line after the second header row ("Rate Qty Total")
      if (
        data.row.section === "head" &&
        data.row.index === 1 &&
        data.column.index === 3
      ) {
        const bottom = data.cell.y + data.cell.height + 2;
        doc.setLineDash([1, 1]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        // doc.setLineDash([]);
      }
      // Bottom dotted line after last row
      if (
        data.row.section === "body" &&
        data.row.index === itemRows?.length - 1 &&
        data.column.index === 0
      ) {
        const bottom = data.cell.y + data.cell.height + 2;
        doc.setLineDash([1, 1]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        doc.setLineDash([]);
      }
    },
  });
  y = doc.lastAutoTable.finalY + 11;
  y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(10);
  doc.text(`Total Item: ${data?.totalItems}`, 0, y);
  y += 10;
  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(11);
  doc.text(`Grand Total: ${documentConfig?.grandTotal}`, 0, y, { align: "left" });
  y += 5;
  // const itemRows = [
  //   [
  //     {
  //       content: "",
  //       colSpan: 4,
  //       styles: {
  //         fontSize: 9,
  //         cellPadding: 2,
  //         lineWidth: 0,
  //         halign: "center",
  //       },
  //     },
  //   ],
  //   ...data.saleitems.flatMap((item) => {
  //     const rows = [];

  //     // Title + Note in single line
  //     let titleWithNote = item.title;
  //     // if (item.imode === "PRODUCT" && item.note) {
  //     //   titleWithNote += ` (${item.note})`;
  //     // }

  //     rows.push([
  //       {
  //         content: titleWithNote,
  //         colSpan: 4,
  //       },
  //     ]);

  //     // Price row
  //     rows.push([
  //       "",
  //       `${CurrencyConverter.toCurrency(item.soldprice || 0, "INR")}`,
  //       `${item.quantity}`,
  //       `${CurrencyConverter.toCurrency(
  //         item.lineTotalSoldPrice || 0,
  //         "INR",
  //       )}`,
  //     ]);

  //     return rows;
  //   }),
  // ];
  // autoTable(doc, {
  //   startY: y,
  //   head: [
  //     [
  //       {
  //         content: "Item",
  //         colSpan: 4,
  //         styles: { halign: "left", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //     ],
  //     [
  //       { content: "", styles: { cellPadding: 0 } },
  //       {
  //         content: "Rate",
  //         styles: { halign: "center", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //       {
  //         content: "Qty",
  //         styles: { halign: "center", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //       {
  //         content: "Total",
  //         styles: { halign: "right", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //     ],
  //   ],
  //   body: itemRows,
  //   theme: "plain",
  //   styles: {
  //     fontSize: 14,
  //     cellPadding: 3,
  //     lineWidth: 0,
  //     font: "NotoSansMono-Regular",
  //   },
  //   columnStyles: {
  //     0: { halign: "left" },
  //     1: { halign: "center" },
  //     2: { halign: "center" },
  //     3: { halign: "right" },
  //   },
  //   margin: { left: 8, right: 8 },
  //   didDrawCell: (data) => {
  //     const pageWidth = doc.internal.pageSize.getWidth();
  //     const left = 8;
  //     const right = pageWidth - 8;

  //     if (
  //       data.row.section === "head" &&
  //       data.row.index === 1 &&
  //       data.column.index === 3
  //     ) {
  //       const bottom = data.cell.y + data.cell.height + 2;
  //       doc.setLineDash([1, 1], 0);
  //       doc.setDrawColor(0);
  //       doc.line(left, bottom, right, bottom);
  //       doc.setLineDash([]);
  //     }
  //     if (
  //       data.row.section === "body" &&
  //       data.row.index === itemRows.length - 1 &&
  //       data.column.index === 0
  //     ) {
  //       const bottom = data.cell.y + data.cell.height + 2;
  //       doc.setLineDash([1, 1], 0);
  //       doc.setDrawColor(0);
  //       doc.line(left, bottom, right, bottom);
  //       doc.setLineDash([]);
  //     }
  //   },
  // });

  // y = doc.lastAutoTable.finalY + 18;
  // // y = checkPageBreak(doc, y);
  // doc.setFontSize(14);
  // doc.text(`Total Item: ${data.totalItems}`, 8, y);
  // y += 24;
  // doc.setFont("NotoSansMono-Bold", "bold").setFontSize(18);
  // doc.text(
  //   `Total Amount: ${CurrencyConverter.toCurrency(
  //     data.grandtotal || 0,
  //     "INR",
  //   )}.00`,
  //   8,
  //   y,
  // );
  // y += 12;

  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(10, y, pageWidth - 10, y);
  y += 10;
  y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(10);
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text("**** YOU SAVED ****", pageWidth / 2, y, { align: "center" });
    y += 10;
  }
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text(`${documentConfig?.totalDiscount}`, pageWidth / 2, y, { align: "center" });
  }
  y += 6;

  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(10);

  const payLabelX = 10;
  const payColonX = 125; // same column for all :
  const payValueX = 132; // amount starts just after :

  const drawPaymentRow = (label, amount, isBold = false) => {
    if (parseAmount(amount) > 0) {
      y = checkPageBreak(doc, y);

      doc.setFont(
        isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular",
        isBold ? "bold" : "normal",
      );
      doc.setFontSize(10);

      doc.text(label, payLabelX, y);
      doc.text(":", payColonX, y);
      doc.text(`${amount}`, payValueX, y);

      y += 11;
    }
  };

  // ---------- PAYMENT MODE HEADER ----------
  if (hasPayment) {
  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(10, y, pageWidth - 10, y);
  doc.setLineDash([]);
  y += 10;

  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(10);
  doc.text("PAYMENT MODE", pageWidth / 2, y + 1, { align: "center" });
  y += 12;

  // ---------- PAYMENT VALUES ----------
  doc.setFont("NotoSansMono-Regular", "normal");

  // Adjusted values
  const cashPaid = (data?.cashamount || 0) - (data?.cashchange || 0);

  const cardPaid = (data?.cardamount || 0) - (data?.cardchange || 0);

  const onlinePaid = (data?.onlineamount || 0) - (data?.onlinechange || 0);

  const upiPaid = (data?.upiamount || 0) - (data?.upichange || 0);

  const balanceDue = (data?.paylateramount || 0) - (data?.paylaterchange || 0);

  // Draw only if value > 0
  drawPaymentRow("Cash", documentConfig?.cashPaid);
  drawPaymentRow("Card",documentConfig?.cardPaid);
  drawPaymentRow("Online Pay", documentConfig?.onlinePaid);
  drawPaymentRow("UPI", documentConfig?.upiPaid);

  // ---------- BALANCE DUE ----------
  if (parseAmount(documentConfig?.balanceDue) > 0) {
    // y += 6;
    drawPaymentRow("Balance Due", documentConfig?.balanceDue, true);
  }
}
  // ---------- FOOTER LINE ----------
  if(hasPayment){
    y -= 5;
    }else{
    y += 2;
  } 
 
  doc.setDrawColor(0);
  doc.setLineDash([1, 1]);
  doc.line(10, y, pageWidth - 10, y);
  doc.setLineDash([]);
  y += 10;

  doc.setFontSize(10);
  doc.text("Thank you for your purchase!", pageWidth / 2, y, {
    align: "center",
  });
  y += 10;
  doc.setFont("NotoSansMono-Bold", "bold");
  doc.text("** Visit us Again **", pageWidth / 2, y, { align: "center" });
  y += 10;
  doc.setFont("NotoSansMono-Regular", "normal");
  doc.text("Powered By", pageWidth / 2, y, { align: "center" });
  y += 10;
  doc.setFontSize(10);
  const sitePrefix = "www.";
  const siteBold = "NegoBill";
  const siteSuffix = ".com";
  const prefixWidth = doc.getTextWidth(sitePrefix);
  const boldWidth = doc.getTextWidth(siteBold);
  const suffixWidth = doc.getTextWidth(siteSuffix);
  const totalWidth = prefixWidth + boldWidth + suffixWidth;
  const startX = (pageWidth - totalWidth) / 2;
  if (storeSettings?.negobillBranding) {
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(sitePrefix, startX, y);
    doc.setFont("NotoSansMono-Bold", "bold");
    doc.text(siteBold, startX + prefixWidth, y);
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(siteSuffix, startX + prefixWidth + boldWidth, y);
    y += 20;
  }
  // setPrint(false);
  return doc;
};

// final

export const Browser2InchSimpleReceipt = async (
  data,
  storeSettings,
  settings,
  timezone = "Asia/Kolkata",
) => {
  // setPrint(true);
  const doc = new jsPDF({
    unit: "pt",
    format: [144, 1000],
  });
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 18;

  // --- Helper: Page Break Check ---
  const checkPageBreak = (y, buffer = 20) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + buffer >= pageHeight) {
      doc.addPage();
      return 14;
    }
    return y;
  };

  // --- HEADER TITLE ---
  doc.setFont("Inter-Bold", "bold").setFontSize(10);
  doc.text("RECEIPT", pageWidth / 2, y, { align: "center" });
  y += 18;

  // --- STORE NAME ---
  doc.setFont("Inter-Regular", "normal").setFontSize(9);
  const wrappedStoreTitle = doc.splitTextToSize(
    settings?.saleProfileData?.title,
    pageWidth - 30,
  );
  wrappedStoreTitle.forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += 13;
  });

  doc.setFontSize(8);
  if (settings?.saleProfileData?.phone) {
    doc.text(
      `Ph: ${settings?.saleProfileData?.phone || ""}`,
      pageWidth / 2,
      y,
      { align: "center" },
    );
    y += 16;
  }

  // y += 16;
  doc.setFontSize(8);

  // --- JOB NO ---
  if (data?.jobNo) {
    doc.setFont("Inter-Bold", "bold");
    doc.text(`Job No:`, 10, y);
    doc.setFont("Inter-Regular", "normal");
    doc.text(String(data.jobNo), 50, y);
    y += 13;
  }

  // --- DATE & TIME ---
  let dateStr = "N/A";
  let timeStr = "N/A";
  if (data?.updatedAt) {
    const converted = DateConverter.convertToTimeZone(data.updatedAt, timezone);
    const formatted = DateConverter.ISOToDisplayFormat(converted);
    dateStr = formatted.date || "-";
    timeStr = formatted.time || "-";
  }

  doc.setFont("Inter-Bold", "bold").setFontSize(8);
  doc.text("Date:", 10, y);
  doc.setFont("Inter-Regular", "normal");
  doc.text(`${dateStr} | ${timeStr}`, 45, y);
  y += 13;

  // --- ADVANCE PAID (moved here below date) ---
  if (data?.totalAdvancePaid && Number(data.totalAdvancePaid) > 0) {
    doc.setFont("Inter-Bold", "bold").setFontSize(8);
    doc.text("Advance Paid:", 10, y);
    doc.setFont("Inter-Bold", "bold");
    doc.text(
      `₹${CurrencyConverter.toCurrency(data.totalAdvancePaid || 0, "INR")}.00`,
      70,
      y,
    );
    y += 5;
  }

  // --- LINE ---
  doc.setLineDash([1, 1], 0);
  doc.line(8, y, pageWidth - 8, y);
  doc.setLineDash([]);
  y += 14;

  // --- CUSTOMER DETAILS HEADING (only if name or phone exists) ---
  if (data?.customer?.name || data?.customer?.phone) {
    doc.setFont("Inter-Bold", "bold").setFontSize(9);
    doc.text("Customer Details", 10, y);
    y += 13;

    // --- CUSTOMER NAME ---
    if (data.customer?.name) {
      doc.setFont("Inter-Bold", "bold").setFontSize(9);
      doc.text("Name:", 10, y);
      doc.setFont("Inter-Regular", "normal");
      doc.text(String(data?.customer?.name), 45, y);
      y += 13;
    }

    // --- CUSTOMER PHONE ---
    if (data.customer?.phone) {
      doc.setFont("Inter-Bold", "bold").setFontSize(9);
      doc.text("Phone:", 10, y);
      doc.setFont("Inter-Regular", "normal");
      doc.text(String(data.customer.phone), 45, y);
      y += 13;
    }

    // --- DOTTED LINE AFTER CUSTOMER DETAILS ---
    doc.setLineDash([1, 1], 0);
    doc.line(8, y, pageWidth - 8, y);
    doc.setLineDash([]);
    y += 16;
  }

  // --- JOB TITLE (task title) ---
  if (data?.title) {
    doc.setFont("Inter-Bold", "bold").setFontSize(9);
    doc.text("Title:", 10, y);
    doc.setFont("Inter-Regular", "normal");
    const wrappedTitle = doc.splitTextToSize(
      String(data.title),
      pageWidth - 20,
    );
    wrappedTitle.forEach((line) => {
      doc.text(line, 40, y);
      y += 13;
    });
    // y += 6;
  }
  // --- TASK LIST ---
  // if (Array.isArray(data?.task) && data.task.length > 0) {
  //   const validTasks = data.task.filter(
  //     (item) => item?.status !== "CANCELLED"
  //   );

  //   if (validTasks.length > 0) {
  //     // doc.setFont("Inter-Bold", "bold").setFontSize(11);
  //     // doc.text("Tasks", 10, y);
  //     // y += 14;

  //     // --- HEADER ---
  //     doc.setFont("Inter-Bold", "bold").setFontSize(10);
  //     doc.text("Title", 10, y);
  //     doc.text("Amount", pageWidth - 10, y, { align: "right" });
  //     y += 12;

  //     doc.setLineDash([1, 1], 0);
  //     doc.line(8, y, pageWidth - 8, y);
  //     doc.setLineDash([]);
  //     y += 10;

  //     // --- ITEMS ---
  //     validTasks.forEach((item) => {
  //       y = checkPageBreak(y);

  //       const title = String(item?.title || "");
  //       const amount = `${CurrencyConverter.toCurrency(item?.amount || 0, "INR")}`;

  //       const wrappedTitle = doc.splitTextToSize(title, pageWidth - 80);

  //       wrappedTitle.forEach((line, index) => {
  //         doc.setFont("Inter-Regular", "normal").setFontSize(10);

  //         doc.text(line, 10, y+2);

  //         if (index === 0) {
  //           doc.text(amount, pageWidth - 10, y+2, { align: "right" });
  //         }

  //         y += 12;
  //       });
  //     });

  //     // y += 10;

  //     // --- LINE ---
  //     doc.setLineDash([1, 1], 0);
  //     doc.line(8, y, pageWidth - 8, y);
  //     doc.setLineDash([]);
  //     y += 16;
  //   }
  // }
  if (data?.dueDate) {
    doc.setFont("Inter-Bold", "bold");
    doc.text("Due Date:", 10, y);
    doc.setFont("Inter-Regular", "normal");
    doc.text(
      `${
        DateConverter.ISOToDisplayFormat(
          DateConverter.convertToTimeZone(data?.dueDate),
        ).date
      }`,
      59,
      y,
    );
    y += 6;
  }
  y += 6;
  // --- LINE ---
  doc.setLineDash([1, 1], 0);
  doc.line(8, y, pageWidth - 8, y);
  doc.setLineDash([]);
  y += 13;
  y = checkPageBreak(y);

  // --- THANK YOU & BRAND ---
  doc.setFont("Inter-Regular", "normal").setFontSize(8);
  doc.text("Thank you!", pageWidth / 2, y, { align: "center" });
  y += 12;
  doc.text("** Visit Again **", pageWidth / 2, y, { align: "center" });
  if (storeSettings?.negobillBranding) {
    y += 12;
    doc.text("Powered by", pageWidth / 2, y, { align: "center" });
    y += 12;

    // --- Website (bold NegoBill) ---
    const sitePrefix = "www.";
    const siteBold = "NegoBill";
    const siteSuffix = ".com";
    const prefixWidth = doc.getTextWidth(sitePrefix);
    const boldWidth = doc.getTextWidth(siteBold);
    const suffixWidth = doc.getTextWidth(siteSuffix);
    const totalWidth = prefixWidth + boldWidth + suffixWidth;
    const startX = (pageWidth - totalWidth) / 2;

    doc.setFont("Inter-Regular", "normal");
    doc.text(sitePrefix, startX, y);
    doc.setFont("Inter-Bold", "bold");
    doc.text(siteBold, startX + prefixWidth, y);
    doc.setFont("Inter-Regular", "normal");
    doc.text(siteSuffix, startX + prefixWidth + boldWidth, y);
  }
  // setPrint(false);
  return doc;
};
